import { useState, useEffect, Fragment, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import Loader from "../../components/loader/Loader";
import CommentEditor from "../../components/commentEditor/CommentEditor";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Dialog, Listbox, Transition, RadioGroup } from "@headlessui/react";
import {
  BriefcaseIcon,
  CalendarIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  CheckIcon,
  CheckCircleIcon,
  ClockIcon,
  InboxIcon,
  InformationCircleIcon as OutlineInformationCircleIcon,
  XIcon,
  XCircleIcon,
  ExclamationCircleIcon,
  ExclamationIcon,
  PhotographIcon,
  TagIcon,
  UserGroupIcon,
  UserAddIcon,
  LocationMarkerIcon,
  KeyIcon,
  PaperClipIcon,
  PlusIcon,
  UploadIcon,
} from "@heroicons/react/outline";
import { InformationCircleIcon } from "@heroicons/react/solid";
import { TrashIcon, PencilIcon } from "@heroicons/react/outline";
import ImageUploading from "react-images-uploading";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./date-picker.css";
import * as api from "./apiService";
import { toast } from "react-toastify";
import { formatJobLocalDateTime } from "../../utils/jobLocalDateTime";

import { useAppSelector } from "../../app/hooks";
import { selectUser } from "../../routes/userProfile/userSlice";
import { set } from "react-hook-form";

const requestPriorities = [
  {
    id: "N",
    title: "NORMAL",
    description:
      "Regular cleaning requests aimed at improving the general appearance of the aircraft.",
    selected: true,
  },
  {
    id: "H",
    title: "HIGH",
    description:
      "Urgent cleaning requests that must be completed at the specified location within the specified time frame.",
    selected: false,
  },
];

const ChevronUpDownIcon = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      className="w-5 h-5 text-gray-400"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M8.25 15L12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9"
      />
    </svg>
  );
};

const availableSteps = [
  { id: 1, name: "Job Details", status: "current", selected: true },
  { id: 2, name: "Services & Retainers", status: "upcoming", selected: false },
  {
    id: 3,
    name: "Additional Instructions",
    status: "upcoming",
    selected: false,
  },
];

const FEE_LABELS = {
  A: "Travel fee",
  F: "Airport/Facility fee",
  G: "General fee",
  V: "Vendor price difference",
  M: "Management fee",
};

const DISCOUNT_LABELS = {
  S: "Service discount",
  A: "Airport discount",
  G: "General discount",
};

const CREATE_JOB_TAG_STYLES = {
  red: "border-red-200 bg-red-50 text-red-700",
  orange: "border-orange-200 bg-orange-50 text-orange-700",
  amber: "border-amber-200 bg-amber-50 text-amber-800",
  indigo: "border-indigo-200 bg-indigo-50 text-indigo-700",
  violet: "border-violet-200 bg-violet-50 text-violet-700",
  fuchsia: "border-fuchsia-200 bg-fuchsia-50 text-fuchsia-700",
  pink: "border-pink-200 bg-pink-50 text-pink-700",
  slate: "border-slate-200 bg-slate-100 text-slate-700",
  lime: "border-lime-200 bg-lime-50 text-lime-700",
  emerald: "border-emerald-200 bg-emerald-50 text-emerald-700",
  cyan: "border-cyan-200 bg-cyan-50 text-cyan-700",
  blue: "border-blue-200 bg-blue-50 text-blue-700",
};

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

function AirplaneIcon({ className = "", ...props }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      {...props}
    >
      <path d="M17.8 19.2 16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 4 2 2 4 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.3c.4-.2.6-.6.5-1.1Z" />
    </svg>
  );
}

export function formatWizardDate(value) {
  if (!value) return "Not specified";
  const date = new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(value);
  const time = `${String(value.getHours()).padStart(2, "0")}:${String(
    value.getMinutes(),
  ).padStart(2, "0")}`;
  return `${date} · ${time}`;
}

function formatTimelineDate(value) {
  if (!value) return "Not specified";
  const date = new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
  }).format(value);
  const time = `${String(value.getHours()).padStart(2, "0")}:${String(
    value.getMinutes(),
  ).padStart(2, "0")}`;
  return `${date} · ${time}`;
}

export function formatTimelineArrival(value, onSite) {
  return onSite ? "On site" : formatTimelineDate(value);
}

export function getServiceWindowLabel(arrival, completeBefore) {
  if (!arrival || !completeBefore || completeBefore < arrival) return null;
  const totalMinutes = Math.round((completeBefore - arrival) / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (minutes === 0) return `${hours}-hour service window`;
  return `${hours}h ${minutes}m service window`;
}

export function getOperationalWindowLabel({
  onSite,
  arrival,
  completeBy,
  departure,
  now = new Date(),
}) {
  return getServiceWindowLabel(
    onSite ? now : arrival,
    onSite ? departure : completeBy,
  );
}

function formatCurrency(value, { allowZero = false } = {}) {
  if (value === null || value === undefined || value === "") return "TBD";

  const amount = Number(value);
  if (
    !Number.isFinite(amount) ||
    amount < 0 ||
    (!allowZero && amount === 0)
  ) {
    return "TBD";
  }
  return amount.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });
}

function getKeyLocationAttachmentUrl(attachment) {
  const file = String(attachment?.file || "");
  if (!file) return "";
  return /^https?:\/\//i.test(file)
    ? file
    : `https://res.cloudinary.com/datidxeqm/${file}`;
}

function KeyLocationEditorDrawer({
  open,
  onClose,
  value,
  onChange,
  onSave,
  hasExistingLocation,
  attachments,
  selectedAttachment,
  onAttachmentChange,
  onRemoveAttachment,
  onRemoveSelectedAttachment,
}) {
  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-[11000]" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-in-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in-out duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-slate-900/30 backdrop-blur-[1px]" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-8 sm:pl-12">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-300"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-300"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <Dialog.Panel className="pointer-events-auto flex h-full w-screen max-w-md flex-col bg-white shadow-xl">
                  <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-5 sm:px-6">
                    <div>
                      <Dialog.Title className="text-lg font-bold text-slate-950">
                        {hasExistingLocation
                          ? "Confirm key location"
                          : "Add key location"}
                      </Dialog.Title>
                      <Dialog.Description className="mt-1 text-sm leading-5 text-slate-500">
                        Confirm where the aircraft key can be found for this
                        tail.
                      </Dialog.Description>
                    </div>
                    <button
                      type="button"
                      onClick={onClose}
                      className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-900"
                    >
                      <span className="sr-only">Close</span>
                      <XIcon className="h-5 w-5" aria-hidden="true" />
                    </button>
                  </div>

                  <div className="flex-1 overflow-y-auto px-5 py-6 sm:px-6">
                    <label htmlFor="create-job-key-location" className="block">
                      <span className="text-sm font-semibold text-slate-800">
                        Key location
                      </span>
                      <textarea
                        id="create-job-key-location"
                        value={value}
                        onChange={(event) => onChange(event.target.value)}
                        rows={6}
                        placeholder="Enter where the aircraft key can be found"
                        className="mt-2 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm leading-6 text-slate-800 shadow-sm outline-none transition focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
                      />
                    </label>
                    <p className="mt-2 text-xs leading-5 text-slate-500">
                      This location will be saved to the tail when the job is
                      created.
                    </p>

                    <div className="mt-6 border-t border-slate-200 pt-5">
                      <h3 className="text-sm font-semibold text-slate-800">
                        Attachment
                      </h3>
                      <p className="mt-1 text-xs leading-5 text-slate-500">
                        Add supporting information for the key location, or
                        remove an attachment that is no longer needed.
                      </p>

                      {attachments.length > 0 && (
                        <ul className="mt-3 space-y-2">
                          {attachments.map((attachment) => (
                            <li
                              key={attachment.id}
                              className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2"
                            >
                              <div className="flex min-w-0 items-center gap-2">
                                <PaperClipIcon
                                  className="h-4 w-4 shrink-0 text-slate-400"
                                  aria-hidden="true"
                                />
                                <span className="truncate text-sm font-medium text-slate-700">
                                  {attachment.name}
                                </span>
                              </div>
                              <div className="flex shrink-0 items-center gap-2 text-xs font-semibold">
                                <a
                                  href={getKeyLocationAttachmentUrl(attachment)}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-sky-700 hover:text-sky-900 hover:underline"
                                >
                                  View
                                </a>
                                <button
                                  type="button"
                                  onClick={() => onRemoveAttachment(attachment)}
                                  className="text-red-600 hover:text-red-800 hover:underline"
                                  aria-label={`Remove ${attachment.name}`}
                                >
                                  Remove
                                </button>
                              </div>
                            </li>
                          ))}
                        </ul>
                      )}

                      {selectedAttachment && (
                        <div className="mt-3 flex items-center justify-between gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2">
                          <div className="flex min-w-0 items-center gap-2">
                            <PaperClipIcon
                              className="h-4 w-4 shrink-0 text-emerald-600"
                              aria-hidden="true"
                            />
                            <span className="truncate text-sm font-medium text-emerald-900">
                              {selectedAttachment.name}
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={onRemoveSelectedAttachment}
                            className="shrink-0 text-xs font-semibold text-red-600 hover:text-red-800 hover:underline"
                            aria-label={`Remove ${selectedAttachment.name}`}
                          >
                            Remove
                          </button>
                        </div>
                      )}

                      {!selectedAttachment && (
                        <label className="mt-3 inline-flex cursor-pointer items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50">
                          <UploadIcon
                            className="h-4 w-4 text-slate-500"
                            aria-hidden="true"
                          />
                          Upload attachment
                          <input
                            type="file"
                            className="sr-only"
                            accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,image/*"
                            aria-label="Upload attachment"
                            onChange={(event) => {
                              const file = event.target.files?.[0] || null;
                              event.target.value = "";
                              onAttachmentChange(file);
                            }}
                          />
                        </label>
                      )}
                    </div>
                  </div>

                  <div className="flex justify-end gap-3 border-t border-slate-200 px-5 py-4 sm:px-6">
                    <button
                      type="button"
                      onClick={onClose}
                      className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={onSave}
                      disabled={!value.trim()}
                      className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Use this location
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

const CreateJob = () => {
  const { estimateId } = useParams();

  const [steps, setSteps] = useState(availableSteps);
  const isStepOneSelected = steps[0].selected;
  const isStepTwoSelected = steps[1].selected;
  const isStepThreeSelected = steps[2].selected;

  const [showServiceActivity, setShowServiceActivity] = useState(true);

  const [showTailAlert, setShowTailAlert] = useState(false);
  const [showTailOpenJobAlert, setShowTailOpenJobAlert] = useState(false);
  const [tailAlert, setTailAlert] = useState(null);
  const [tailOpenJobAlert, setTailOpenJobAlert] = useState(null);
  const [keyLocation, setKeyLocation] = useState("");
  const [keyLocationDraft, setKeyLocationDraft] = useState("");
  const [keyLocationLoading, setKeyLocationLoading] = useState(false);
  const [keyLocationEditorOpen, setKeyLocationEditorOpen] = useState(false);
  const [keyLocationDirty, setKeyLocationDirty] = useState(false);
  const [keyLocationAttachments, setKeyLocationAttachments] = useState([]);
  const [keyLocationAttachmentsDraft, setKeyLocationAttachmentsDraft] =
    useState([]);
  const [keyLocationAttachment, setKeyLocationAttachment] = useState(null);
  const [keyLocationAttachmentDraft, setKeyLocationAttachmentDraft] =
    useState(null);
  const [removedKeyLocationAttachmentIds, setRemovedKeyLocationAttachmentIds] =
    useState([]);
  const [
    removedKeyLocationAttachmentIdsDraft,
    setRemovedKeyLocationAttachmentIdsDraft,
  ] = useState([]);

  const [loading, setLoading] = useState(false);
  const [createJobMessage, setCreateJobMessage] = useState(null);
  const [createdJob, setCreatedJob] = useState(null);
  const [jobDetails, setJobDetails] = useState({});
  const [errorMessage, setErrorMessage] = useState(null);

  const [activeServiceScope, setActiveServiceScope] = useState(null);
  const [projectedPricing, setProjectedPricing] = useState(null);
  const [projectedPricingLoading, setProjectedPricingLoading] = useState(false);
  const [projectedPricingError, setProjectedPricingError] = useState(null);

  const [enableFlightawareTracking, setEnableFlightawareTracking] =
    useState(false);

  const [tailNumber, setTailNumber] = useState("");
  const [tailNumberErrorMessage, setTailNumberErrorMessage] = useState(null);
  const [ident, setIdent] = useState("");
  const [requestedByErrorMessage, setRequestedByErrorMessage] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [aircraftTypes, setAircraftTypes] = useState([]);
  const [airports, setAirports] = useState([]);
  const [fbos, setFbos] = useState([]);
  const [allFbos, setAllFbos] = useState([]);

  const [serviceActivities, setServiceActivities] = useState([]);

  const [services, setServices] = useState([]);
  const [retainerServices, setRetainerServices] = useState([]);

  const [interiorServices, setInteriorServices] = useState([]);
  const [exteriorServices, setExteriorServices] = useState([]);
  const [otherServices, setOtherServices] = useState([]);

  const [interiorRetainerServices, setInteriorRetainerServices] = useState([]);
  const [exteriorRetainerServices, setExteriorRetainerServices] = useState([]);
  const [otherRetainerServices, setOtherRetainerServices] = useState([]);

  const [servicesErrorMessage, setServicesErrorMessage] = useState(null);

  const [tags, setTags] = useState([]);

  const [customerSelected, setCustomerSelected] = useState(null);
  const [aircraftTypeSelected, setAircraftTypeSelected] = useState(null);
  const [airportSelected, setAirportSelected] = useState(null);
  const [fboSelected, setFboSelected] = useState(null);

  const [estimatedArrivalDate, setEstimatedArrivalDate] = useState(null);
  const [estimatedDepartureDate, setEstimatedDepartureDate] = useState(null);
  const [completeByDate, setCompleteByDate] = useState(null);
  const [comment, setComment] = useState("");
  const [commentHtml, setCommentHtml] = useState("");

  const [requestedBy, setRequestedBy] = useState("");

  const [images, setImages] = useState([]);

  const [estimatedArrivalDateOpen, setEstimatedArrivalDateOpen] =
    useState(false);
  const [estimatedDepartureDateOpen, setEstimatedDepartureDateOpen] =
    useState(false);
  const [completeByDateOpen, setCompleteByDateOpen] = useState(false);

  const [onSite, setOnSite] = useState(false);

  const [customerSearchTerm, setCustomerSearchTerm] = useState("");
  const [aircraftSearchTerm, setAircraftSearchTerm] = useState("");
  const [airportSearchTerm, setAirportSearchTerm] = useState("");
  const [fboSearchTerm, setFboSearchTerm] = useState("");
  const [customerPurchaseOrder, setCustomerPurchaseOrder] = useState("");

  const [
    showExteriorLevel2Recommendation,
    setShowExteriorLevel2Recommendation,
  ] = useState(false);
  const [arrivedFlightCount, setArrivedFlightCount] = useState(0);

  const [airportFees, setAirportFees] = useState([]);
  const [fboFees, setFboFees] = useState([]);
  const [customerFollowerEmails, setCustomerFollowerEmails] = useState([]);
  const [jobFollowerEmails, setJobFollowerEmails] = useState([]);
  const [customerFollowerEmailSearchTerm, setCustomerFollowerEmailSearchTerm] =
    useState("");

  const [isRequestPriorityEnabled, setIsRequestPriorityEnabled] =
    useState(false);

  const [hideAddonsServices, setHideAddonsServices] = useState(false);

  const [selectedPriority, setSelectedPriority] = useState(
    requestPriorities[0],
  );

  const [showExteriorWetWashWarning, setShowExteriorWetWashWarning] =
    useState(false);

  const [showRegularServices, setShowRegularServices] = useState(false);
  const [showRetainerServices, setShowRetainerServices] = useState(false);
  const [activeServiceCatalog, setActiveServiceCatalog] = useState(null);

  const currentUser = useAppSelector(selectUser);

  const navigate = useNavigate();

  const filteredFbos = fboSearchTerm
    ? fbos.filter((item) =>
        item.name.toLowerCase().includes(fboSearchTerm.toLowerCase()),
      )
    : fbos;

  const selectedRegularServices = useMemo(
    () =>
      [...interiorServices, ...exteriorServices, ...otherServices].filter(
        (service) => service.selected === true,
      ),
    [interiorServices, exteriorServices, otherServices],
  );
  const selectedRetainerServices = useMemo(
    () =>
      [
        ...interiorRetainerServices,
        ...exteriorRetainerServices,
        ...otherRetainerServices,
      ].filter((service) => service.selected === true),
    [interiorRetainerServices, exteriorRetainerServices, otherRetainerServices],
  );
  const selectedRegularServiceKey = selectedRegularServices
    .map((service) => service.id)
    .sort((a, b) => a - b)
    .join(",");
  const selectedRetainerServiceKey = selectedRetainerServices
    .map((service) => service.id)
    .sort((a, b) => a - b)
    .join(",");
  const effectiveCustomerId = currentUser.customerId || customerSelected?.id;
  const canSeeProjectedPricing = currentUser.canSeePrice === true;
  const serviceWindowLabel = getOperationalWindowLabel({
    onSite,
    arrival: estimatedArrivalDate,
    completeBy: completeByDate,
    departure: estimatedDepartureDate,
  });

  const projectedPriceByServiceId = useMemo(
    () =>
      Object.fromEntries(
        (projectedPricing?.services || []).map((service) => [
          String(service.id),
          service.price,
        ]),
      ),
    [projectedPricing],
  );

  useEffect(() => {
    setSelectedPriority(requestPriorities[0]);
  }, []);

  useEffect(() => {
    const newSteps = [...steps];

    newSteps[0].selected = true;
    newSteps[0].status = "current";
    newSteps[1].status = "upcoming";
    newSteps[2].status = "upcoming";

    newSteps[1].selected = false;
    newSteps[2].selected = false;

    setSteps(newSteps);
  }, []);

  useEffect(() => {
    getJobInfo();
  }, []);

  useEffect(() => {
    //Basic throttling
    let timeoutID = setTimeout(() => {
      getTailLookups();
    }, 300);

    return () => {
      clearTimeout(timeoutID);
    };
  }, [tailNumber]);

  useEffect(() => {
    const normalizedTailNumber = tailNumber.trim();
    let isCurrent = true;

    setKeyLocationDirty(false);
    setKeyLocationEditorOpen(false);
    setKeyLocationAttachment(null);
    setKeyLocationAttachmentDraft(null);
    setRemovedKeyLocationAttachmentIds([]);
    setRemovedKeyLocationAttachmentIdsDraft([]);

    if (normalizedTailNumber.length <= 2) {
      setKeyLocation("");
      setKeyLocationDraft("");
      setKeyLocationAttachments([]);
      setKeyLocationAttachmentsDraft([]);
      setKeyLocationLoading(false);
      return () => {
        isCurrent = false;
      };
    }

    setKeyLocationLoading(true);
    const timeoutID = setTimeout(async () => {
      try {
        const { data } = await api.getTailKeyLocation(normalizedTailNumber, {
          customer_id: effectiveCustomerId,
          aircraft_type_id: aircraftTypeSelected?.id,
        });
        if (isCurrent) {
          const location = data?.key_location || "";
          const attachments = data?.attachments || [];
          setKeyLocation(location);
          setKeyLocationDraft(location);
          setKeyLocationAttachments(attachments);
          setKeyLocationAttachmentsDraft(attachments);
        }
      } catch (error) {
        if (isCurrent) {
          setKeyLocation("");
          setKeyLocationDraft("");
          setKeyLocationAttachments([]);
          setKeyLocationAttachmentsDraft([]);
        }
      } finally {
        if (isCurrent) setKeyLocationLoading(false);
      }
    }, 300);

    return () => {
      isCurrent = false;
      clearTimeout(timeoutID);
    };
  }, [aircraftTypeSelected?.id, effectiveCustomerId, tailNumber]);

  useEffect(() => {
    //Basic throttling
    let timeoutID = setTimeout(() => {
      searchAirports();
    }, 500);

    return () => {
      clearTimeout(timeoutID);
    };
  }, [airportSearchTerm]);

  useEffect(() => {
    //Basic throttling
    let timeoutID = setTimeout(() => {
      searchCustomers();
    }, 500);

    return () => {
      clearTimeout(timeoutID);
    };
  }, [customerSearchTerm]);

  useEffect(() => {
    if (customerSelected) {
      let timeoutID = setTimeout(() => {
        searchCustomerFollowerEmails();
      }, 500);

      return () => {
        clearTimeout(timeoutID);
      };
    }
  }, [customerFollowerEmailSearchTerm]);

  useEffect(() => {
    if (customerSelected) {
      let timeoutID = setTimeout(() => {
        initialSearchCustomerFollowerEmails();
      }, 500);

      return () => {
        clearTimeout(timeoutID);
      };
    }
  }, [customerSelected]);

  useEffect(() => {
    //Basic throttling
    let timeoutID = setTimeout(() => {
      searchAircrafts();
    }, 500);

    return () => {
      clearTimeout(timeoutID);
    };
  }, [aircraftSearchTerm]);

  useEffect(() => {
    let isCurrent = true;

    if (
      !canSeeProjectedPricing ||
      !effectiveCustomerId ||
      !aircraftTypeSelected?.id ||
      !airportSelected?.id ||
      !fboSelected?.id
    ) {
      setProjectedPricing(null);
      setProjectedPricingError(null);
      setProjectedPricingLoading(false);
      return () => {
        isCurrent = false;
      };
    }

    setProjectedPricingLoading(true);
    setProjectedPricingError(null);
    const timeoutID = setTimeout(async () => {
      try {
        const { data } = await api.getProjectedJobPricing({
          customer_id: effectiveCustomerId,
          aircraft_type_id: aircraftTypeSelected.id,
          airport_id: airportSelected.id,
          fbo_id: fboSelected.id,
          service_ids: selectedRegularServiceKey
            ? selectedRegularServiceKey.split(",").map(Number)
            : [],
          retainer_service_ids: selectedRetainerServiceKey
            ? selectedRetainerServiceKey.split(",").map(Number)
            : [],
        });
        if (isCurrent) setProjectedPricing(data);
      } catch (error) {
        if (isCurrent) {
          setProjectedPricing(null);
          setProjectedPricingError(
            "Projected pricing is temporarily unavailable.",
          );
        }
      } finally {
        if (isCurrent) setProjectedPricingLoading(false);
      }
    }, 250);

    return () => {
      isCurrent = false;
      clearTimeout(timeoutID);
    };
  }, [
    aircraftTypeSelected?.id,
    airportSelected?.id,
    canSeeProjectedPricing,
    effectiveCustomerId,
    fboSelected?.id,
    selectedRegularServiceKey,
    selectedRetainerServiceKey,
  ]);

  const searchAirports = async () => {
    try {
      const { data } = await api.searchAirports({ name: airportSearchTerm });

      setAirports(data.results);
    } catch (err) {
      toast.error("Unable to search airports");
    }
  };

  const searchCustomerFollowerEmails = async () => {
    try {
      const { data } = await api.searchCustomerFollowerEmails({
        customer_id: customerSelected?.id,
        email: customerFollowerEmailSearchTerm,
      });

      setCustomerFollowerEmails(data.results);
    } catch (err) {
      toast.error("Unable to search customer follower emails");
    }
  };

  const initialSearchCustomerFollowerEmails = async () => {
    try {
      const { data } = await api.searchCustomerFollowerEmails({
        customer_id: customerSelected?.id,
        email: "",
      });

      setCustomerFollowerEmails(data.results);

      const persistentEmails = data.results.filter(
        (email) => email.is_persistent === true,
      );
      setJobFollowerEmails(persistentEmails);
    } catch (err) {
      toast.error("Unable to search customer follower emails");
    }
  };

  const searchCustomers = async () => {
    try {
      const { data } = await api.getCustomers({ name: customerSearchTerm });

      setCustomers(data.results);
    } catch (err) {
      toast.error("Unable to search customers");
    }
  };

  const searchAircrafts = async () => {
    try {
      const { data } = await api.searchAircraftTypes({
        name: aircraftSearchTerm,
      });

      setAircraftTypes(data.results);
    } catch (err) {
      toast.error("Unable to search aircrafts");
    }
  };

  const getTailLookups = async () => {
    if (tailNumber?.length > 2 && !estimateId) {
      const { data } = await api.getTailAircraftLookup(tailNumber);

      if (data) {
        setAircraftTypeSelected({
          id: data.aircraft_id,
          name: data.aircraft_name,
        });
        setAircraftSearchTerm(data.aircraft_name);

        if (
          currentUser.isAdmin ||
          currentUser.isSuperUser ||
          currentUser.isAccountManager ||
          currentUser.isInternalCoordinator
        ) {
          getServicesAndRetainers(data.customer_id);

          setCustomerSelected({
            id: data.customer_id,
            name: data.customer_name,
          });
          setCustomerSearchTerm(data.customer_name);

          try {
            const response1 = await api.getCustomerDetails(data.customer_id);

            setIsRequestPriorityEnabled(
              response1.data.settings.enable_request_priority,
            );
            setHideAddonsServices(response1.data.settings.hide_addons_services);
          } catch (err) {
            toast.error("Unable to get customer details");
          }
        }
      }

      if (
        currentUser.isAdmin ||
        currentUser.isSuperUser ||
        currentUser.isAccountManager ||
        currentUser.isInternalCoordinator ||
        currentUser.isCustomer
      ) {
        const r = await api.getTailOpenJobLookup(tailNumber);
        if (r.data?.jobId > 0) {
          setShowTailOpenJobAlert(true);
          setTailOpenJobAlert(r.data);
        } else {
          setShowTailOpenJobAlert(false);
          setTailOpenJobAlert(null);
        }
      }

      if (
        currentUser.isAdmin ||
        currentUser.isSuperUser ||
        currentUser.isAccountManager ||
        currentUser.isInternalCoordinator
      ) {
        const response = await api.getTailAlertLookup(tailNumber);
        if (response.data?.id > 0) {
          setShowTailAlert(true);
          setTailAlert(response.data);
        } else {
          setShowTailAlert(false);
          setTailAlert(null);
        }
      }

      const request = {
        tail_number: tailNumber,
      };

      const response2 = await api.getTailServiceHistory(request);

      //iterate through response2.data.results and remove duplicates entry.
      //A duplicate entry has the same service name and the same purchase order number
      const uniqueServiceActivities = [];

      for (let i = 0; i < response2.data.results.length; i++) {
        const serviceActivity = response2.data.results[i];
        const found = uniqueServiceActivities.some(
          (el) =>
            el.service_name === serviceActivity.service_name &&
            el.purchase_order === serviceActivity.purchase_order,
        );

        if (!found) {
          uniqueServiceActivities.push(serviceActivity);
        }
      }

      setServiceActivities(uniqueServiceActivities);

      const response3 = await api.getIdentTailLookup(tailNumber);

      if (response3) {
        setIdent(response3.data);
      } else {
        setIdent("");
      }
    }
  };

  const getJobInfo = async () => {
    setLoading(true);

    try {
      const { data } = await api.getJobFormInfo();
      setCustomers(data.customers);
      setAircraftTypes(data.aircraft_types);
      setAirports(data.airports);
      setAllFbos(data.fbos);
      setFbos(data.fbos);
      setTags(data.tags);

      if (data.customer_id) {
        getServicesAndRetainers(data.customer_id);
        setIsRequestPriorityEnabled(data.is_enable_request_priority);
        setHideAddonsServices(data.hide_addons_services);
      }

      // if estimateId is passed in, get the estimate info and pre-populate the form
      if (estimateId) {
        const response = await api.getEstimateDetail(estimateId);
        setTailNumber(response.data.tailNumber);
        setCustomerSelected(response.data.customer);
        setAircraftTypeSelected(response.data.aircraftType);
        setAirportSelected(response.data.airport);
        setFboSelected(response.data.fbo);

        const interior = [];
        const exterior = [];
        const other = [];

        response.data.services.forEach((service) => {
          if (service.category === "I") {
            interior.push(service);
          } else if (service.category === "E") {
            exterior.push(service);
          } else {
            other.push(service);
          }
        });

        setInteriorServices(interior);
        setExteriorServices(exterior);
        setOtherServices(other);

        if (response.data.services.length > 0) {
          setActiveServiceCatalog("regular");
          setShowRegularServices(true);
          setShowRetainerServices(false);
        }
      }

      setLoading(false);
    } catch (error) {
      setLoading(false);
      setErrorMessage(error.message);
    }
  };

  const tailExteriorLevel2Checker = async () => {
    let selectedCustomer = customerSelected;

    if (currentUser.customerId) {
      selectedCustomer = {
        id: currentUser.customerId,
      };
    }
    const request = {
      tail_number: tailNumber,
      customer_id: selectedCustomer.id,
      ident: ident,
    };

    try {
      const { data } = await api.tailExteriorLevel2Checker(request);

      setShowExteriorLevel2Recommendation(data.show_recommendation);
      setArrivedFlightCount(data.arrived_flights_count);
    } catch (error) {
      toast.error("Unable to check tail exterior level 2");
    }
  };

  const createJob = async (routeName) => {
    let selectedServices = [];
    selectedServices = selectedServices.concat(
      interiorServices.filter((service) => service.selected === true),
    );
    selectedServices = selectedServices.concat(
      exteriorServices.filter((service) => service.selected === true),
    );
    selectedServices = selectedServices.concat(
      otherServices.filter((service) => service.selected === true),
    );

    let selectedRetainerServices = [];
    selectedRetainerServices = selectedRetainerServices.concat(
      interiorRetainerServices.filter((service) => service.selected === true),
    );
    selectedRetainerServices = selectedRetainerServices.concat(
      exteriorRetainerServices.filter((service) => service.selected === true),
    );
    selectedRetainerServices = selectedRetainerServices.concat(
      otherRetainerServices.filter((service) => service.selected === true),
    );

    const selectedTags = tags.filter((tag) => tag.selected === true);

    let selectedCustomer = customerSelected;

    if (currentUser.customerId) {
      selectedCustomer = {
        id: currentUser.customerId,
      };
    }

    setTailNumberErrorMessage(null);
    setServicesErrorMessage(null);

    if (tailNumber.length === 0) {
      alert("Tail number is required");
      return;
    }

    if (!selectedCustomer) {
      alert("Customer is required");
      return;
    }

    if (!aircraftTypeSelected) {
      alert("Aircraft type is required");
      return;
    }

    if (!airportSelected) {
      alert("Airport is required");
      return;
    }

    if (!fboSelected) {
      alert("FBO is required");
      return;
    }

    if (
      selectedServices.length === 0 &&
      selectedRetainerServices.length === 0
    ) {
      alert("Select at least one service or retainer");
      return;
    }

    if (currentUser.promptRequestedBy && requestedBy.length === 0) {
      alert("Enter your name and email");
      return;
    }

    //Add validation so if a estimatedDepartureDate date is entered on a date BEFORE the estimatedArrivalDate, then the estimatedDepartureDate is not Valid. Add message "ETD Date/Time is before ETA Date/Time
    if (estimatedArrivalDate && estimatedDepartureDate) {
      if (estimatedArrivalDate > estimatedDepartureDate) {
        alert("ETD Date/Time is before ETA Date/Time");
        return;
      }
    }

    //Add validation when completeByDate is a date before estimatedArrivalDate
    if (estimatedArrivalDate && completeByDate) {
      if (completeByDate < estimatedArrivalDate) {
        alert("Complete By Date/Time is before ETA Date/Time");
        return;
      }
    }

    //add validation when completeByDate is a date after estimatedDepartureDate. Departure must be before complete by date
    if (estimatedDepartureDate && completeByDate) {
      if (completeByDate > estimatedDepartureDate) {
        alert("Complete By Date/Time is after ETD Date/Time");
        return;
      }
    }

    //Add validation when completeByDate must be after before estimatedArrivalDate if present. If onSite is true, then completeByBefore cannot be before today's date
    if (estimatedArrivalDate && completeByDate) {
      if (completeByDate < estimatedArrivalDate) {
        alert("Complete By Date/Time is before ETA Date/Time");
        return;
      }
    }

    if (onSite && completeByDate) {
      if (completeByDate < new Date()) {
        alert("Complete By Date/Time is before today's date");
        return;
      }
    }

    if (estimatedDepartureDate) {
      if (estimatedDepartureDate < new Date()) {
        alert("ETD Date/Time is before today's date");
        return;
      }
    }

    const selectedServiceIds = selectedServices.map((service) => service.id);
    const selectedRetainerServiceIds = selectedRetainerServices.map(
      (service) => service.id,
    );
    const selectedTagIds = selectedTags.map((tag) => tag.id);

    //create a comma separated string of jobFollowerEmails
    const jobFollowerEmailsString = jobFollowerEmails
      .map((email) => email.email)
      .join(",");

    const formData = new FormData();

    formData.append("tail_number", tailNumber);
    formData.append("customer_id", selectedCustomer.id);
    formData.append("aircraft_type_id", aircraftTypeSelected.id);
    formData.append("airport_id", airportSelected.id);
    formData.append("fbo_id", fboSelected.id);
    formData.append("estimated_arrival_date", estimatedArrivalDate);
    formData.append("estimated_departure_date", estimatedDepartureDate);
    formData.append("complete_by_date", completeByDate);

    const formattedArrivalDate = formatJobLocalDateTime(estimatedArrivalDate);
    const formattedDepartureDate = formatJobLocalDateTime(
      estimatedDepartureDate,
    );
    const formattedCompleteByDate = formatJobLocalDateTime(completeByDate);

    if (formattedArrivalDate) {
      formData.append("formatted_arrival_date", formattedArrivalDate);
    }

    if (formattedDepartureDate) {
      formData.append("formatted_departure_date", formattedDepartureDate);
    }

    if (formattedCompleteByDate) {
      formData.append("formatted_complete_by_date", formattedCompleteByDate);
    }

    formData.append("services", selectedServiceIds);
    formData.append("retainer_services", selectedRetainerServiceIds);
    formData.append("tags", selectedTagIds);
    formData.append("comment", comment);
    if (comment.trim()) {
      formData.append("comment_html", commentHtml);
    }
    formData.append("on_site", onSite);
    formData.append("requested_by", requestedBy);
    formData.append("customer_purchase_order", customerPurchaseOrder);
    formData.append("priority", selectedPriority.id);
    formData.append("follower_emails", jobFollowerEmailsString);
    formData.append("ident", ident);

    if (keyLocationDirty && keyLocation.trim()) {
      formData.append("key_location", keyLocation.trim());
      if (keyLocationAttachment) {
        formData.append("key_location_attachment", keyLocationAttachment);
      }
      if (removedKeyLocationAttachmentIds.length > 0) {
        formData.append(
          "removed_key_location_attachment_ids",
          removedKeyLocationAttachmentIds.join(","),
        );
      }
    }

    if (estimateId) {
      formData.append("estimate_id", estimateId);
    }

    images.forEach((image) => {
      if (image.file.size < 10000000) {
        // less than 10MB
        formData.append("image", image.file);
      }
    });

    setLoading(true);
    setCreateJobMessage("Creating job. Please wait...");

    try {
      const { data } = await api.createJob(formData);

      setLoading(false);
      setCreateJobMessage(
        `A new job with purchase order ${data.purchase_order} has been added to the queue.`,
      );
      setCreatedJob({
        id: data.id,
        purchaseOrder: data.purchase_order,
        tailNumber,
        customerName:
          data.customer_name ||
          selectedCustomer?.name ||
          currentUser.customerName ||
          "",
      });

      if (routeName === "assignments") {
        navigate("/jobs/" + data.id + "/assignments");
      }
    } catch (error) {
      setLoading(false);
      setCreateJobMessage(null);
      setCreatedJob(null);
      toast.error("Unable to create job");
    }
  };

  const handleToggleEstimatedArrivalDate = () => {
    setEstimatedArrivalDateOpen(!estimatedArrivalDateOpen);
  };

  const handleToggleEstimatedDepartureDate = () => {
    setEstimatedDepartureDateOpen(!estimatedDepartureDateOpen);
  };

  const handleToggleCompleteByDate = () => {
    setCompleteByDateOpen(!completeByDateOpen);
  };

  const handleEstimatedArrivalDateChange = (date, event) => {
    setOnSite(false);
    setEstimatedArrivalDate(date);
  };

  const handleEstimatedDepartureDateChange = (date, event) => {
    setEstimatedDepartureDate(date);
  };

  const handleCompleteByDateChange = (date, event) => {
    setCompleteByDate(date);
  };

  const onChangePhoto = (imageList, addUpdateIndex) => {
    setImages(imageList);
  };

  const handleSetOnSite = () => {
    setOnSite(!onSite);
    setEstimatedArrivalDate(null);
  };

  const handleOpenKeyLocationEditor = () => {
    setKeyLocationDraft(keyLocation);
    setKeyLocationAttachmentsDraft(keyLocationAttachments);
    setKeyLocationAttachmentDraft(keyLocationAttachment);
    setRemovedKeyLocationAttachmentIdsDraft(
      removedKeyLocationAttachmentIds,
    );
    setKeyLocationEditorOpen(true);
  };

  const getSelectedServiceCount = (...serviceGroups) => {
    return serviceGroups.reduce((total, group) => {
      return (
        total + group.filter((service) => service.selected === true).length
      );
    }, 0);
  };

  const updateActiveServiceCatalogAfterSelection = ({
    nextInteriorServices = interiorServices,
    nextExteriorServices = exteriorServices,
    nextOtherServices = otherServices,
    nextInteriorRetainerServices = interiorRetainerServices,
    nextExteriorRetainerServices = exteriorRetainerServices,
    nextOtherRetainerServices = otherRetainerServices,
  }) => {
    const selectedRegularServiceCount = getSelectedServiceCount(
      nextInteriorServices,
      nextExteriorServices,
      nextOtherServices,
    );
    const selectedRetainerServiceCount = getSelectedServiceCount(
      nextInteriorRetainerServices,
      nextExteriorRetainerServices,
      nextOtherRetainerServices,
    );

    if (selectedRegularServiceCount > 0) {
      setActiveServiceCatalog("regular");
      setShowRetainerServices(false);
      return;
    }

    if (selectedRetainerServiceCount > 0) {
      setActiveServiceCatalog("retainer");
      setShowRegularServices(false);
      return;
    }

    setActiveServiceCatalog(null);
  };

  const handleServiceChange = (service) => {
    if (activeServiceCatalog === "retainer") {
      return;
    }

    if (service.category === "I") {
      const interiorServicesUpdated = interiorServices.map((el) => {
        if (el.id === service.id) {
          return { ...el, selected: !el.selected };
        }
        return el;
      });

      setInteriorServices(interiorServicesUpdated);
      updateActiveServiceCatalogAfterSelection({
        nextInteriorServices: interiorServicesUpdated,
      });
    } else if (service.category === "E") {
      if (
        !service.selected &&
        service.is_exterior_wet_wash &&
        !airportSelected?.is_wet_wash_allowed
      ) {
        setShowExteriorWetWashWarning(true);
      }

      const exteriorServicesUpdated = exteriorServices.map((el) => {
        if (el.id === service.id) {
          return { ...el, selected: !el.selected };
        }
        return el;
      });

      const selectedExteriorService = exteriorServicesUpdated.find(
        (el) => el.id === service.id,
      );

      if (
        selectedExteriorService.selected === false &&
        selectedExteriorService.is_exterior_wet_wash
      ) {
        setShowExteriorWetWashWarning(false);
      }

      setExteriorServices(exteriorServicesUpdated);
      updateActiveServiceCatalogAfterSelection({
        nextExteriorServices: exteriorServicesUpdated,
      });
    } else {
      const otherServicesUpdated = otherServices.map((el) => {
        if (el.id === service.id) {
          return { ...el, selected: !el.selected };
        }
        return el;
      });

      setOtherServices(otherServicesUpdated);
      updateActiveServiceCatalogAfterSelection({
        nextOtherServices: otherServicesUpdated,
      });
    }
  };

  const handleRetainerServiceChange = (retainerService) => {
    if (activeServiceCatalog === "regular") {
      return;
    }

    if (retainerService.category === "I") {
      const interiorRetainerServicesUpdated = interiorRetainerServices.map(
        (el) => {
          if (el.id === retainerService.id) {
            return { ...el, selected: !el.selected };
          }
          return el;
        },
      );

      setInteriorRetainerServices(interiorRetainerServicesUpdated);
      updateActiveServiceCatalogAfterSelection({
        nextInteriorRetainerServices: interiorRetainerServicesUpdated,
      });
    } else if (retainerService.category === "E") {
      const exteriorRetainerServicesUpdated = exteriorRetainerServices.map(
        (el) => {
          if (el.id === retainerService.id) {
            return { ...el, selected: !el.selected };
          }
          return el;
        },
      );

      setExteriorRetainerServices(exteriorRetainerServicesUpdated);
      updateActiveServiceCatalogAfterSelection({
        nextExteriorRetainerServices: exteriorRetainerServicesUpdated,
      });
    } else {
      const otherRetainerServicesUpdated = otherRetainerServices.map((el) => {
        if (el.id === retainerService.id) {
          return { ...el, selected: !el.selected };
        }
        return el;
      });

      setOtherRetainerServices(otherRetainerServicesUpdated);
      updateActiveServiceCatalogAfterSelection({
        nextOtherRetainerServices: otherRetainerServicesUpdated,
      });
    }
  };

  const handleTagChange = (tag) => {
    const tagsUpdated = tags.map((el) => {
      if (el.id === tag.id) {
        el.selected = !el.selected;
      }
      return el;
    });

    setTags(tagsUpdated);
  };

  const addAnotherJob = () => {
    setLoading(true);
    setCreateJobMessage(null);
    setCreatedJob(null);
    setTailNumber("");
    setKeyLocation("");
    setKeyLocationDraft("");
    setKeyLocationDirty(false);
    setKeyLocationEditorOpen(false);
    setKeyLocationAttachments([]);
    setKeyLocationAttachmentsDraft([]);
    setKeyLocationAttachment(null);
    setKeyLocationAttachmentDraft(null);
    setRemovedKeyLocationAttachmentIds([]);
    setRemovedKeyLocationAttachmentIdsDraft([]);
    const newSteps = [...steps];

    newSteps[0].selected = true;
    newSteps[0].status = "current";
    newSteps[1].status = "upcoming";

    newSteps[1].selected = false;
    newSteps[2].selected = false;

    setSteps(newSteps);

    window.location.reload();
    setLoading(false);
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  };

  const deleteTailAlert = async () => {
    await api.deleteTailAlert(tailAlert.id);
    setShowTailAlert(false);
  };

  const handleGoToNextStep = (currentStep) => {
    let selectedServices = [];
    selectedServices = selectedServices.concat(
      interiorServices.filter((service) => service.selected === true),
    );
    selectedServices = selectedServices.concat(
      exteriorServices.filter((service) => service.selected === true),
    );
    selectedServices = selectedServices.concat(
      otherServices.filter((service) => service.selected === true),
    );

    let selectedRetainerServices = [];
    selectedRetainerServices = selectedRetainerServices.concat(
      interiorRetainerServices.filter((service) => service.selected === true),
    );
    selectedRetainerServices = selectedRetainerServices.concat(
      exteriorRetainerServices.filter((service) => service.selected === true),
    );
    selectedRetainerServices = selectedRetainerServices.concat(
      otherRetainerServices.filter((service) => service.selected === true),
    );

    const selectedTags = tags.filter((tag) => tag.selected === true);

    let selectedCustomer = customerSelected;

    if (currentUser.customerId) {
      selectedCustomer = {
        id: currentUser.customerId,
      };
    }

    if (currentStep.id === 1) {
      if (tailNumber.length === 0) {
        alert("Tail number is required");
        return;
      }

      if (!selectedCustomer) {
        alert("Customer is required");
        return;
      }

      if (!aircraftTypeSelected) {
        alert("Aircraft type is required");
        return;
      }

      if (!airportSelected) {
        alert("Airport is required");
        return;
      }

      if (!fboSelected) {
        alert("FBO is required");
        return;
      }

      if (currentUser.promptRequestedBy && requestedBy.length === 0) {
        alert("Enter your name and email");
        return;
      }

      tailExteriorLevel2Checker();
    } else if (currentStep.id === 2) {
      if (
        selectedServices.length === 0 &&
        selectedRetainerServices.length === 0
      ) {
        alert("Select at least one service or retainer");
        return;
      }
    }

    const newSteps = [...steps];

    newSteps[0].selected = false;
    newSteps[1].selected = false;
    newSteps[2].selected = false;

    if (currentStep.id === 1) {
      newSteps[1].selected = true;
      newSteps[1].status = "current";
      newSteps[0].status = "complete";
    } else if (currentStep.id === 2) {
      newSteps[2].selected = true;
      newSteps[2].status = "current";
      newSteps[1].status = "complete";
    }

    setSteps(newSteps);

    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  };

  const handleGotoPreviousStep = (currentStep) => {
    const newSteps = [...steps];

    newSteps[0].selected = false;
    newSteps[1].selected = false;
    newSteps[2].selected = false;

    if (currentStep.id === 2) {
      newSteps[0].selected = true;
      newSteps[0].status = "current";
      newSteps[1].status = "upcoming";
    } else if (currentStep.id === 3) {
      newSteps[1].selected = true;
      newSteps[1].status = "current";
      newSteps[2].status = "upcoming";
    }

    setSteps(newSteps);

    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  };

  const handleCustomerSelectedChange = async (customer) => {
    setCustomerSelected(customer);
    getServicesAndRetainers(customer.id);

    try {
      const { data } = await api.getCustomerDetails(customer.id);

      setIsRequestPriorityEnabled(data.settings.enable_request_priority);
      setHideAddonsServices(data.settings.hide_addons_services);
    } catch (err) {
      toast.error("Unable to get customer details");
    }

    if (currentUser.showAirportFees && airportSelected) {
      const request = {
        airport_id: airportSelected.id,
        customer_id: customer.id,
      };

      try {
        const { data } = await api.getAirportCustomerFees(request);

        setAirportFees(data);
      } catch (err) {
        //ignore
      }
    }

    if (currentUser.showAirportFees && fboSelected) {
      const request = {
        fbo_id: fboSelected.id,
        customer_id: customer.id,
      };

      try {
        const { data } = await api.getFBOCustomerFees(request);

        setFboFees(data);
      } catch (err) {
        //ignore
      }
    }
  };

  const getServicesAndRetainers = async (customerId) => {
    try {
      const { data } = await api.getCustomerRetainerServices(customerId);

      setServices(data.services);
      setRetainerServices(data.retainer_services);
      setShowRegularServices(false);
      setShowRetainerServices(false);
      setActiveServiceCatalog(null);
      setShowExteriorWetWashWarning(false);

      const interior = [];
      const exterior = [];
      const other = [];

      data.services.forEach((service) => {
        if (service.category === "I") {
          interior.push(service);
        } else if (service.category === "E") {
          exterior.push(service);
        } else {
          other.push(service);
        }
      });

      setInteriorServices(interior);
      setExteriorServices(exterior);
      setOtherServices(other);

      const interiorRetainer = [];
      const exteriorRetainer = [];
      const otherRetainer = [];

      data.retainer_services.forEach((retainerService) => {
        if (retainerService.category === "I") {
          interiorRetainer.push(retainerService);
        } else if (retainerService.category === "E") {
          exteriorRetainer.push(retainerService);
        } else {
          otherRetainer.push(retainerService);
        }
      });

      setInteriorRetainerServices(interiorRetainer);
      setExteriorRetainerServices(exteriorRetainer);
      setOtherRetainerServices(otherRetainer);
    } catch (err) {
      toast.error("Unable to get customer services");
    }
  };

  const handleFboSelectedChange = async (fbo) => {
    setFboSelected(fbo);

    let customer_id = null;

    if (currentUser.customerId) {
      customer_id = currentUser.customerId;
    } else if (customerSelected) {
      customer_id = customerSelected.id;
    }

    if (currentUser.showAirportFees && customer_id) {
      const request = {
        fbo_id: fbo.id,
        customer_id: customer_id,
      };

      try {
        const { data } = await api.getFBOCustomerFees(request);

        setFboFees(data);
      } catch (err) {
        //ignore
      }
    }
  };

  const handleAddFollowerEmail = (email) => {
    if (email) {
      //only add email if email.email does not exist in jobFollowerEmails
      if (
        jobFollowerEmails.filter((e) => e.email === email.email).length === 0
      ) {
        setJobFollowerEmails([...jobFollowerEmails, email]);
      }
    } else {
      if (customerFollowerEmailSearchTerm) {
        // customerFollowerEmailSearchTerm must be a valid email address and not have commas
        if (
          !customerFollowerEmailSearchTerm.includes(",") &&
          validateEmail(customerFollowerEmailSearchTerm)
        ) {
          const newEmail = {
            email: customerFollowerEmailSearchTerm,
          };

          //only add email if email.email does not exist in jobFollowerEmails
          if (
            jobFollowerEmails.filter((e) => e.email === newEmail.email)
              .length === 0
          ) {
            setJobFollowerEmails([...jobFollowerEmails, newEmail]);
          }
        }
      }
    }
  };

  const validateEmail = (email) => {
    const re = /\S+@\S+\.\S+/;
    return re.test(email);
  };

  const handleRemoveFollowerEmail = (email) => {
    const updatedEmails = jobFollowerEmails.filter(
      (e) => e.email !== email.email,
    );

    setJobFollowerEmails(updatedEmails);
  };

  const handleAirportSelectedChange = async (airport) => {
    setAirportSelected(airport);

    const request = {
      airport_id: airport.id,
    };

    try {
      const { data } = await api.searchFbos(request);

      if (data.results.length > 0) {
        setFbos(data.results);
      } else {
        setFbos(allFbos);
      }
    } catch (err) {
      toast.error("Unable to get Fbos");
    }

    let customer_id = null;

    if (currentUser.customerId) {
      customer_id = currentUser.customerId;
    } else if (customerSelected) {
      customer_id = customerSelected.id;
    }

    if (currentUser.showAirportFees && customer_id) {
      const request = {
        airport_id: airport.id,
        customer_id: customer_id,
      };

      try {
        const { data } = await api.getAirportCustomerFees(request);

        setAirportFees(data);
      } catch (err) {
        //ignore
      }
    }
  };

  const isRetainerCustomer = retainerServices.length > 0;

  const regularCatalogDisabled = activeServiceCatalog === "retainer";
  const retainerCatalogDisabled = activeServiceCatalog === "regular";

  const regularSelectedCount = getSelectedServiceCount(
    interiorServices,
    exteriorServices,
    otherServices,
  );
  const retainerSelectedCount = getSelectedServiceCount(
    interiorRetainerServices,
    exteriorRetainerServices,
    otherRetainerServices,
  );

  const toggleRegularServicesSection = () => {
    if (regularCatalogDisabled) {
      return;
    }

    setShowRegularServices(!showRegularServices);
    setShowRetainerServices(false);
  };

  const toggleRetainerServicesSection = () => {
    if (retainerCatalogDisabled) {
      return;
    }

    setShowRetainerServices(!showRetainerServices);
    setShowRegularServices(false);
  };

  const openServiceScope = (service, catalog) => {
    setActiveServiceScope({ service, catalog });
  };

  const renderRegularServiceCard = (service) => {
    const price = projectedPriceByServiceId[String(service.id)];

    return (
      <article
        key={service.id}
        className={classNames(
          "overflow-hidden rounded-xl border transition",
          service.selected
            ? "border-blue-200 bg-blue-50"
            : "border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm",
        )}
      >
        <button
          type="button"
          aria-pressed={service.selected}
          onClick={() => handleServiceChange(service)}
          className="flex w-full items-start justify-between gap-4 px-4 py-4 text-left"
        >
          <span className="flex min-w-0 items-start gap-3">
            <span
              className={classNames(
                "mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border",
                service.selected
                  ? "border-blue-600 bg-blue-600 text-white"
                  : "border-slate-300 bg-white text-transparent",
              )}
            >
              <CheckIcon className="h-3.5 w-3.5" aria-hidden="true" />
            </span>
            <span>
              <strong className="block text-sm font-semibold text-slate-900">
                {service.name}
              </strong>
              {showExteriorLevel2Recommendation &&
                service.name.includes("Basic Exterior") && (
                  <span className="mt-1 inline-flex rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-red-700 ring-1 ring-inset ring-red-200">
                    Not recommended
                  </span>
                )}
              {showExteriorLevel2Recommendation &&
                service.is_exterior_detail_level_2 && (
                  <span className="mt-1 inline-flex rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-emerald-700 ring-1 ring-inset ring-emerald-200">
                    Recommended
                  </span>
                )}
            </span>
          </span>
          {canSeeProjectedPricing && (
            <span className="shrink-0 text-sm font-bold text-slate-900">
              {projectedPricingLoading && price === undefined
                ? "—"
                : formatCurrency(price)}
            </span>
          )}
        </button>
        <button
          type="button"
          onClick={() => openServiceScope(service, "regular")}
          className="ml-12 -mt-2 block pb-4 text-left text-xs font-semibold text-[#0284C7] hover:text-sky-700"
        >
          View scope
        </button>
      </article>
    );
  };

  const renderRetainerServiceCard = (service) => {
    return (
      <article
        key={service.id}
        className={classNames(
          "overflow-hidden rounded-xl border transition",
          service.selected
            ? "border-blue-200 bg-blue-50"
            : "border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm",
        )}
      >
        <button
          type="button"
          aria-pressed={service.selected}
          onClick={() => handleRetainerServiceChange(service)}
          className="flex w-full items-start justify-between gap-4 px-4 py-4 text-left"
        >
          <span className="flex min-w-0 items-start gap-3">
            <span
              className={classNames(
                "mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border",
                service.selected
                  ? "border-blue-600 bg-blue-600 text-white"
                  : "border-slate-300 bg-white text-transparent",
              )}
            >
              <CheckIcon className="h-3.5 w-3.5" aria-hidden="true" />
            </span>
            <strong className="text-sm font-semibold text-slate-900">
              {service.name}
            </strong>
          </span>
          <span className="shrink-0 rounded-full bg-violet-50 px-2.5 py-1 text-xs font-semibold text-violet-700 ring-1 ring-inset ring-violet-200">
            Retainer
          </span>
        </button>
        <button
          type="button"
          onClick={() => openServiceScope(service, "retainer")}
          className="ml-12 -mt-2 block pb-4 text-left text-xs font-semibold text-[#0284C7] hover:text-sky-700"
        >
          View scope
        </button>
      </article>
    );
  };

  const renderRegularServicesContent = () => (
    <>
      {interiorServices.length > 0 && (
        <>
          <div className="mt-6 text-sm font-bold text-slate-900">Interior</div>
          <div className="mt-3 grid grid-cols-1 gap-3 xl:grid-cols-2">
            {interiorServices.map((service) =>
              renderRegularServiceCard(service),
            )}
          </div>
        </>
      )}

      {exteriorServices.length > 0 && (
        <>
          <div className="my-6 border-t border-slate-200"></div>
          <div className="text-sm font-bold text-slate-900">Exterior</div>
          {showExteriorLevel2Recommendation && (
            <div className="mb-4 mt-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3">
              <div className="flex items-start gap-3">
                <ExclamationCircleIcon
                  className="mt-0.5 h-5 w-5 shrink-0 text-red-500"
                  aria-hidden="true"
                />
                <div className="min-w-0">
                  <h3 className="text-sm font-semibold text-red-800">
                    Exterior Service Recommendation
                  </h3>
                  <p className="mt-1 text-sm leading-5 text-red-700">
                    This aircraft has completed{" "}
                    <span className="font-semibold">{arrivedFlightCount}</span>{" "}
                    flight(s) since its last Exterior Detail.
                  </p>
                </div>
              </div>
            </div>
          )}

          {showExteriorWetWashWarning && (
            <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-4">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <ExclamationCircleIcon
                    className="h-6 w-6 text-red-500"
                    aria-hidden="true"
                  />
                </div>

                <div className="ml-3 flex-1">
                  <h3 className="text-sm font-semibold text-red-800">
                    Important Note
                  </h3>

                  <p className="mt-1 text-sm text-red-700">
                    Wet Wash is not permitted at this airport. Only Dry Wash is
                    allowed. You have the following options:
                  </p>

                  <ul className="mt-4 list-disc space-y-2 pl-5 text-sm text-red-700">
                    <li>
                      Select{" "}
                      <span className="font-medium mr-1">Exterior Level 2</span>
                      instead. The local detailing team will perform a Dry Wash
                      for this aircraft.
                    </li>
                    <li>
                      If a Wet Wash is strictly required, submit the job request
                      with Exterior Wet Wash selected.
                      <br></br> Our team will track this tail and schedule the
                      Wet Wash at the next airport where it is permitted.
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          <div className="mt-3 grid grid-cols-1 gap-3 xl:grid-cols-2">
            {exteriorServices.map((service) =>
              renderRegularServiceCard(service),
            )}
          </div>
        </>
      )}

      {!(currentUser?.is_job_submitter_only && hideAddonsServices) &&
        otherServices.length > 0 && (
          <>
            <div className="my-6 border-t border-slate-200"></div>
            <div className="text-sm font-bold text-slate-900">Add-ons</div>
            <div className="mt-3 grid grid-cols-1 gap-3 xl:grid-cols-2">
              {otherServices.map((service) =>
                renderRegularServiceCard(service),
              )}
            </div>
          </>
        )}
    </>
  );

  const renderRetainerServicesContent = () => (
    <>
      {interiorRetainerServices.length > 0 && (
        <>
          <div className="mt-6 text-sm font-bold text-slate-900">Interior</div>
          <div className="mt-3 grid grid-cols-1 gap-3 xl:grid-cols-2">
            {interiorRetainerServices.map((service) =>
              renderRetainerServiceCard(service),
            )}
          </div>
        </>
      )}

      {exteriorRetainerServices.length > 0 && (
        <>
          <div className="my-6 border-t border-slate-200"></div>
          <div className="text-sm font-bold text-slate-900">Exterior</div>
          <div className="mt-3 grid grid-cols-1 gap-3 xl:grid-cols-2">
            {exteriorRetainerServices.map((service) =>
              renderRetainerServiceCard(service),
            )}
          </div>
        </>
      )}

      {!(currentUser?.is_job_submitter_only && hideAddonsServices) &&
        otherRetainerServices.length > 0 && (
          <>
            <div className="my-6 border-t border-slate-200"></div>
            <div className="text-sm font-bold text-slate-900">Add-ons</div>
            <div className="mt-3 grid grid-cols-1 gap-3 xl:grid-cols-2">
              {otherRetainerServices.map((service) =>
                renderRetainerServiceCard(service),
              )}
            </div>
          </>
        )}
    </>
  );

  const renderCatalogSection = ({
    title,
    description,
    icon,
    isOpen,
    isDisabled,
    selectedCount,
    onToggle,
    children,
  }) => (
    <div
      className={classNames(
        "rounded-xl border bg-white text-left shadow-sm transition",
        isDisabled
          ? "border-gray-200 bg-gray-50 opacity-60"
          : "border-gray-200 hover:border-red-200 hover:shadow-md",
        isOpen ? "ring-1 ring-red-100" : "",
      )}
    >
      <button
        type="button"
        onClick={onToggle}
        disabled={isDisabled}
        className="flex w-full items-center justify-between gap-4 p-6 text-left disabled:cursor-not-allowed"
      >
        <div className="flex items-center gap-4">
          <div
            className={classNames(
              "flex h-12 w-12 shrink-0 items-center justify-center rounded-full",
              isDisabled
                ? "bg-gray-100 text-gray-400"
                : "bg-red-50 text-red-500",
            )}
          >
            {icon}
          </div>
          <div>
            <div className="flex items-center gap-3">
              <h3 className="text-xl font-semibold text-gray-900">{title}</h3>
              {selectedCount > 0 && (
                <span className="rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-semibold text-blue-700">
                  {selectedCount} selected
                </span>
              )}
            </div>
            <p className="mt-1 text-sm text-gray-500">{description}</p>
          </div>
        </div>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={2}
          stroke="currentColor"
          className={classNames(
            "h-5 w-5 shrink-0 text-gray-500 transition-transform",
            isOpen ? "rotate-180" : "",
          )}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M19.5 8.25l-7.5 7.5-7.5-7.5"
          />
        </svg>
      </button>

      {isOpen && !isDisabled && (
        <div className="border-t border-gray-100 px-6 pb-6">{children}</div>
      )}
    </div>
  );

  return (
    <div>
      {loading && (
        <>
          <Loader />
          {createJobMessage && (
            <div className="text-gray-500 text-lg m-auto text-center">
              {createJobMessage}
            </div>
          )}
        </>
      )}

      {!loading && createJobMessage && (
        <main className="job-create-redesign job-create-page-canvas min-h-screen px-4 pb-16 pt-8 sm:px-6 sm:pt-12 lg:pb-12">
          <section className="mx-auto max-w-2xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="px-6 py-8 text-center sm:px-10 sm:py-10">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full border border-emerald-200 bg-emerald-50 text-emerald-600">
                <CheckIcon className="h-7 w-7" aria-hidden="true" />
              </div>
              <p className="mt-5 text-xs font-bold uppercase tracking-[0.12em] text-emerald-700">
                Job created
              </p>
              <h1 className="mt-2 text-2xl font-bold tracking-tight text-slate-950 sm:text-3xl">
                Your job is ready
              </h1>
              <p className="mx-auto mt-2 max-w-lg text-sm leading-6 text-slate-500 sm:text-base">
                The job has been added to the queue and is ready for your team.
              </p>

              <dl className="mx-auto mt-7 grid max-w-xl gap-px overflow-hidden rounded-xl border border-slate-200 bg-slate-200 text-left sm:grid-cols-3">
                <div className="bg-slate-50 px-4 py-3">
                  <dt className="text-xs font-semibold uppercase tracking-[0.06em] text-slate-500">
                    Purchase order
                  </dt>
                  <dd className="mt-1 truncate text-sm font-bold text-slate-900">
                    {createdJob?.purchaseOrder || "—"}
                  </dd>
                </div>
                <div className="bg-slate-50 px-4 py-3">
                  <dt className="text-xs font-semibold uppercase tracking-[0.06em] text-slate-500">
                    Tail number
                  </dt>
                  <dd className="mt-1 truncate text-sm font-bold text-slate-900">
                    {createdJob?.tailNumber || "—"}
                  </dd>
                </div>
                <div className="bg-slate-50 px-4 py-3">
                  <dt className="text-xs font-semibold uppercase tracking-[0.06em] text-slate-500">
                    Customer
                  </dt>
                  <dd className="mt-1 truncate text-sm font-bold text-slate-900">
                    {createdJob?.customerName || "—"}
                  </dd>
                </div>
              </dl>

              {currentUser.isCustomer && (
                <div className="mx-auto mt-6 max-w-xl rounded-xl border border-sky-100 bg-sky-50 px-4 py-3 text-left text-sm leading-6 text-slate-600">
                  <p>
                    Our account managers have been notified of your request.
                  </p>
                  <p className="mt-1">
                    To receive job updates, enable SMS notifications in your{" "}
                    <Link
                      to="/user-settings/profile"
                      className="font-semibold text-[#0284C7] hover:text-[#0369A1] hover:underline"
                    >
                      profile settings
                    </Link>
                    .
                  </p>
                </div>
              )}
            </div>

            <div className="flex flex-col-reverse gap-3 border-t border-slate-200 bg-slate-50 px-6 py-5 sm:flex-row sm:items-center sm:justify-between sm:px-10">
              <button
                type="button"
                onClick={() => navigate("/")}
                className="inline-flex h-10 items-center justify-center rounded-lg px-3 text-sm font-semibold text-slate-600 transition hover:bg-slate-200 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2"
              >
                Back to Home
              </button>
              <div className="flex flex-col-reverse gap-3 sm:flex-row">
                <button
                  type="button"
                  onClick={() => addAnotherJob()}
                  className="inline-flex h-10 items-center justify-center rounded-lg border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                >
                  Add another job
                </button>
                <button
                  type="button"
                  onClick={() => navigate(`/jobs/${createdJob?.id}/details`)}
                  disabled={!createdJob?.id}
                  className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-transparent bg-[#E3222A] px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-[#C91D24] focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  View job
                  <ArrowRightIcon className="h-4 w-4" aria-hidden="true" />
                </button>
              </div>
            </div>
          </section>
        </main>
      )}

      {!loading && errorMessage && (
        <div className="text-gray-500 m-auto text-center mt-20">
          {errorMessage}
        </div>
      )}

      {!loading && errorMessage == null && createJobMessage == null && (
        <main className="job-create-redesign job-create-page-canvas mx-auto min-h-screen max-w-[1500px] px-4 pb-16 pt-5 sm:px-6 sm:pt-6 lg:pb-12">
          <div>
            <Link
              to="/"
              className="mb-3 inline-flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-slate-800"
            >
              <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" />
              Jobs Queue
            </Link>
            <h1 className="text-3xl font-bold tracking-tight text-slate-950">
              Create Job
            </h1>
            <p className="mt-1 text-base text-slate-500">
              Create the job, confirm its scope, and decide whether to continue
              with assignment.
            </p>
          </div>
          <nav aria-label="Progress" className="mt-6">
            <ol className="grid grid-cols-1 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm sm:grid-cols-3">
              {steps.map((step) => (
                <li
                  key={step.name}
                  className="relative border-b border-slate-200 last:border-b-0 sm:border-b-0 sm:border-r sm:last:border-r-0"
                >
                  <div
                    className={classNames(
                      "flex min-h-[64px] items-center gap-2 px-3 py-3 sm:gap-3 sm:px-6",
                      step.status === "current" ? "bg-red-50/60" : "bg-white",
                    )}
                    aria-current={
                      step.status === "current" ? "step" : undefined
                    }
                  >
                    <span
                      className={classNames(
                        "flex h-8 w-8 shrink-0 items-center justify-center rounded-full border text-sm font-semibold",
                        step.status === "complete"
                          ? "border-emerald-500 bg-emerald-500 text-white"
                          : step.status === "current"
                            ? "border-red-500 text-red-600"
                            : "border-slate-300 text-slate-400",
                      )}
                    >
                      {step.status === "complete" ? (
                        <CheckIcon className="h-4 w-4" aria-hidden="true" />
                      ) : (
                        step.id
                      )}
                    </span>
                    <span
                      className={classNames(
                        "text-left text-sm font-semibold leading-5 sm:text-base",
                        step.status === "current"
                          ? "text-red-600"
                          : step.status === "complete"
                            ? "text-slate-900"
                            : "text-slate-500",
                      )}
                    >
                      {step.name}
                    </span>
                  </div>
                </li>
              ))}
            </ol>
          </nav>

          {isStepOneSelected && (
            <>
              <div className="mt-6 grid grid-cols-1 items-stretch gap-6 lg:grid-cols-[minmax(0,1fr)_22rem]">
                <div className="overflow-visible rounded-xl border border-slate-200 bg-white shadow-sm">
                  <div className="grid grid-cols-1 md:grid-cols-2">
                    <div className="space-y-5 p-5 sm:p-6">
                      <div>
                        <label
                          htmlFor="tailNumber"
                          className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500"
                        >
                          Tail Number
                        </label>
                        <div className="mt-2">
                          <input
                            type="text"
                            value={tailNumber}
                            onChange={(e) =>
                              setTailNumber(e.target.value.toLocaleUpperCase())
                            }
                            name="tailNumber"
                            id="tailNumber"
                            placeholder="Please type the full tail number"
                            className="block h-11 w-full rounded-lg border-slate-300 text-sm font-medium text-slate-800 shadow-sm placeholder:font-normal placeholder:text-slate-400 focus:border-sky-500 focus:ring-sky-500"
                          />
                          {tailNumberErrorMessage && (
                            <p className="text-red-500 text-xs font-semibold mt-2">
                              {tailNumberErrorMessage}
                            </p>
                          )}
                        </div>
                      </div>

                      {!currentUser.isCustomer && (
                        <div className="mt-1">
                          <Listbox
                            value={customerSelected}
                            onChange={handleCustomerSelectedChange}
                          >
                            {({ open }) => (
                              <>
                                <Listbox.Label className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">
                                  Customer
                                </Listbox.Label>
                                <div className="relative mt-2">
                                  <Listbox.Button
                                    className="relative w-full cursor-default rounded-lg border
                                                                        border-slate-300 bg-white py-2.5 pl-3 pr-10 text-left
                                                                        shadow-sm focus:border-sky-500 focus:outline-none
                                                                        focus:ring-1 focus:ring-sky-500 text-sm font-medium text-slate-800"
                                  >
                                    <span className="block truncate">
                                      {customerSelected
                                        ? customerSelected.name
                                        : "Select customer"}
                                    </span>
                                    <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                                      <ChevronUpDownIcon
                                        className="h-5 w-5 text-gray-400"
                                        aria-hidden="true"
                                      />
                                    </span>
                                  </Listbox.Button>

                                  <Transition
                                    show={open}
                                    as={Fragment}
                                    leave="transition ease-in duration-100"
                                    leaveFrom="opacity-100"
                                    leaveTo="opacity-0"
                                  >
                                    <Listbox.Options className="absolute z-30 mt-2 max-h-64 w-full overflow-auto rounded-lg border border-slate-200 bg-white py-1 text-sm shadow-xl ring-1 ring-black/5 focus:outline-none">
                                      <div className="relative">
                                        <div className="sticky top-0 z-20 bg-white p-2">
                                          <div className="relative">
                                            <input
                                              type="text"
                                              name="search"
                                              id="customer-search"
                                              placeholder="Search customers"
                                              value={customerSearchTerm}
                                              onChange={(e) =>
                                                setCustomerSearchTerm(
                                                  e.target.value,
                                                )
                                              }
                                              className="block h-10 w-full rounded-lg border-slate-300 bg-white pl-3 pr-16 text-sm font-normal text-slate-800 shadow-sm placeholder:text-slate-400 focus:border-sky-500 focus:ring-sky-500"
                                            />
                                            <div className="absolute inset-y-0 right-0 flex items-center pr-2">
                                              {customerSearchTerm && (
                                                <svg
                                                  xmlns="http://www.w3.org/2000/svg"
                                                  className="mr-1 h-5 w-5 cursor-pointer text-slate-400"
                                                  viewBox="0 0 20 20"
                                                  fill="currentColor"
                                                  onClick={() => {
                                                    setCustomerSearchTerm("");
                                                  }}
                                                >
                                                  <path
                                                    fillRule="evenodd"
                                                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                                    clipRule="evenodd"
                                                  />
                                                </svg>
                                              )}
                                              <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                className="h-5 w-5 text-slate-400"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                stroke="currentColor"
                                              >
                                                <path
                                                  strokeLinecap="round"
                                                  strokeLinejoin="round"
                                                  strokeWidth="2"
                                                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                                />
                                              </svg>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                      {customers.map((customer) => (
                                        <Listbox.Option
                                          key={customer.id}
                                          className={({ active }) =>
                                            classNames(
                                              active
                                                ? "text-white bg-red-600"
                                                : "text-gray-900",
                                              "relative cursor-default select-none py-2.5 pl-3 pr-9 text-sm",
                                            )
                                          }
                                          value={customer}
                                        >
                                          {({ selected, active }) => (
                                            <>
                                              <span
                                                className={classNames(
                                                  selected
                                                    ? "font-semibold"
                                                    : "font-normal",
                                                  "block truncate",
                                                )}
                                              >
                                                {customer.name}
                                              </span>
                                              {selected ? (
                                                <span
                                                  className={classNames(
                                                    active
                                                      ? "text-white"
                                                      : "text-red-600",
                                                    "absolute inset-y-0 right-0 flex items-center pr-4",
                                                  )}
                                                >
                                                  <CheckIcon
                                                    className="h-5 w-5"
                                                    aria-hidden="true"
                                                  />
                                                </span>
                                              ) : null}
                                            </>
                                          )}
                                        </Listbox.Option>
                                      ))}
                                    </Listbox.Options>
                                  </Transition>
                                </div>
                              </>
                            )}
                          </Listbox>
                        </div>
                      )}

                      {isRequestPriorityEnabled && (
                        <RadioGroup
                          value={selectedPriority}
                          onChange={setSelectedPriority}
                        >
                          <RadioGroup.Label className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">
                            Priority
                          </RadioGroup.Label>

                          <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2">
                            {requestPriorities.map((priority) => (
                              <RadioGroup.Option
                                key={priority.id}
                                value={priority}
                                className={({ active }) =>
                                  classNames(
                                    active
                                      ? "border-red-500 ring-2 ring-red-500"
                                      : "border-gray-300",
                                    "relative flex cursor-pointer rounded-lg border bg-white p-3 shadow-sm focus:outline-none",
                                  )
                                }
                              >
                                {({ checked, active }) => (
                                  <>
                                    <span className="flex flex-1">
                                      <span className="flex flex-col">
                                        <RadioGroup.Label
                                          as="span"
                                          className="block text-sm font-semibold text-slate-900"
                                        >
                                          {priority.title}
                                        </RadioGroup.Label>
                                        <RadioGroup.Description
                                          as="span"
                                          className="mt-1 flex items-center text-xs leading-4 text-slate-500"
                                        >
                                          {priority.description}
                                        </RadioGroup.Description>
                                      </span>
                                    </span>
                                    <CheckCircleIcon
                                      className={classNames(
                                        !checked ? "invisible" : "",
                                        "h-5 w-5 text-red-600",
                                      )}
                                      aria-hidden="true"
                                    />
                                    <span
                                      className={classNames(
                                        active ? "border" : "border-2",
                                        checked
                                          ? "border-red-600"
                                          : "border-transparent",
                                        "pointer-events-none absolute -inset-px rounded-lg",
                                      )}
                                      aria-hidden="true"
                                    />
                                  </>
                                )}
                              </RadioGroup.Option>
                            ))}
                          </div>
                        </RadioGroup>
                      )}

                      <div className="mt-1">
                        <Listbox
                          value={aircraftTypeSelected}
                          onChange={setAircraftTypeSelected}
                        >
                          {({ open }) => (
                            <>
                              <Listbox.Label className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">
                                Aircraft Type
                              </Listbox.Label>
                              <div className="relative mt-2">
                                <Listbox.Button
                                  className="relative w-full cursor-default rounded-lg border
                                                                    border-slate-300 bg-white py-2.5 pl-3 pr-10 text-left
                                                                    shadow-sm focus:border-sky-500 focus:outline-none
                                                                    focus:ring-1 focus:ring-sky-500 text-sm font-medium text-slate-800"
                                >
                                  <span className="block truncate">
                                    {aircraftTypeSelected
                                      ? aircraftTypeSelected.name
                                      : "Select aircraft type"}
                                  </span>
                                  <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                                    <ChevronUpDownIcon
                                      className="h-5 w-5 text-gray-400"
                                      aria-hidden="true"
                                    />
                                  </span>
                                </Listbox.Button>

                                <Transition
                                  show={open}
                                  as={Fragment}
                                  leave="transition ease-in duration-100"
                                  leaveFrom="opacity-100"
                                  leaveTo="opacity-0"
                                >
                                  <Listbox.Options className="absolute z-30 mt-2 max-h-64 w-full overflow-auto rounded-lg border border-slate-200 bg-white py-1 text-sm shadow-xl ring-1 ring-black/5 focus:outline-none">
                                    <div className="relative">
                                      <div className="sticky top-0 z-20 bg-white p-2">
                                        <div className="relative">
                                          <input
                                            type="text"
                                            name="search"
                                            id="aircraft-search"
                                            placeholder="Search aircraft types"
                                            value={aircraftSearchTerm}
                                            onChange={(e) =>
                                              setAircraftSearchTerm(
                                                e.target.value,
                                              )
                                            }
                                            className="block h-10 w-full rounded-lg border-slate-300 bg-white pl-3 pr-16 text-sm font-normal text-slate-800 shadow-sm placeholder:text-slate-400 focus:border-sky-500 focus:ring-sky-500"
                                          />
                                          <div className="absolute inset-y-0 right-0 flex items-center pr-2">
                                            {aircraftSearchTerm && (
                                              <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                className="mr-1 h-5 w-5 cursor-pointer text-slate-400"
                                                viewBox="0 0 20 20"
                                                fill="currentColor"
                                                onClick={() => {
                                                  setAircraftSearchTerm("");
                                                }}
                                              >
                                                <path
                                                  fillRule="evenodd"
                                                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                                  clipRule="evenodd"
                                                />
                                              </svg>
                                            )}
                                            <svg
                                              xmlns="http://www.w3.org/2000/svg"
                                              className="h-5 w-5 text-slate-400"
                                              fill="none"
                                              viewBox="0 0 24 24"
                                              stroke="currentColor"
                                            >
                                              <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth="2"
                                                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                              />
                                            </svg>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                    {aircraftTypes.map((aircraftType) => (
                                      <Listbox.Option
                                        key={aircraftType.id}
                                        className={({ active }) =>
                                          classNames(
                                            active
                                              ? "text-white bg-red-600"
                                              : "text-gray-900",
                                            "relative cursor-default select-none py-2.5 pl-3 pr-9 text-sm",
                                          )
                                        }
                                        value={aircraftType}
                                      >
                                        {({ selected, active }) => (
                                          <>
                                            <span
                                              className={classNames(
                                                selected
                                                  ? "font-semibold"
                                                  : "font-normal",
                                                "block truncate",
                                              )}
                                            >
                                              {aircraftType.name}
                                            </span>
                                            {selected ? (
                                              <span
                                                className={classNames(
                                                  active
                                                    ? "text-white"
                                                    : "text-red-600",
                                                  "absolute inset-y-0 right-0 flex items-center pr-4",
                                                )}
                                              >
                                                <CheckIcon
                                                  className="h-5 w-5"
                                                  aria-hidden="true"
                                                />
                                              </span>
                                            ) : null}
                                          </>
                                        )}
                                      </Listbox.Option>
                                    ))}
                                  </Listbox.Options>
                                </Transition>
                              </div>
                            </>
                          )}
                        </Listbox>
                      </div>
                      <div className="mt-1">
                        <Listbox
                          value={airportSelected}
                          onChange={handleAirportSelectedChange}
                        >
                          {({ open }) => (
                            <>
                              <Listbox.Label className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">
                                Airport
                              </Listbox.Label>
                              <div className="relative mt-2">
                                <Listbox.Button
                                  className="relative w-full cursor-default rounded-lg border
                                                                    border-slate-300 bg-white py-2.5 pl-3 pr-10 text-left
                                                                    shadow-sm focus:border-sky-500 focus:outline-none
                                                                    focus:ring-1 focus:ring-sky-500 text-sm font-medium text-slate-800"
                                >
                                  <span className="block truncate">
                                    {airportSelected
                                      ? `${airportSelected.initials || airportSelected.code || ""}${airportSelected.initials || airportSelected.code ? " · " : ""}${airportSelected.name}`
                                      : "Select airport"}
                                  </span>
                                  <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                                    <ChevronUpDownIcon
                                      className="h-5 w-5 text-gray-400"
                                      aria-hidden="true"
                                    />
                                  </span>
                                </Listbox.Button>

                                <Transition
                                  show={open}
                                  as={Fragment}
                                  leave="transition ease-in duration-100"
                                  leaveFrom="opacity-100"
                                  leaveTo="opacity-0"
                                >
                                  <Listbox.Options className="absolute z-30 mt-2 max-h-64 w-full overflow-auto rounded-lg border border-slate-200 bg-white py-1 text-sm shadow-xl ring-1 ring-black/5 focus:outline-none">
                                    <div className="relative">
                                      <div className="sticky top-0 z-20 bg-white p-2">
                                        <div className="relative">
                                          <input
                                            type="text"
                                            name="search"
                                            id="airport-search"
                                            placeholder="Search airport code or name"
                                            value={airportSearchTerm}
                                            onChange={(e) =>
                                              setAirportSearchTerm(
                                                e.target.value,
                                              )
                                            }
                                            className="block h-10 w-full rounded-lg border-slate-300 bg-white pl-3 pr-10 text-sm font-normal text-slate-800 shadow-sm placeholder:text-slate-400 focus:border-sky-500 focus:ring-sky-500"
                                          />
                                          <div className="absolute inset-y-0 right-0 flex items-center pr-2">
                                            {airportSearchTerm && (
                                              <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                className="h-5 w-5 cursor-pointer text-slate-400"
                                                viewBox="0 0 20 20"
                                                fill="currentColor"
                                                onClick={() => {
                                                  setAirportSearchTerm("");
                                                }}
                                              >
                                                <path
                                                  fillRule="evenodd"
                                                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                                  clipRule="evenodd"
                                                />
                                              </svg>
                                            )}
                                            <svg
                                              xmlns="http://www.w3.org/2000/svg"
                                              className="ml-1 h-5 w-5 text-slate-400"
                                              fill="none"
                                              viewBox="0 0 24 24"
                                              stroke="currentColor"
                                            >
                                              <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth="2"
                                                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                              />
                                            </svg>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                    {airports.map((airport) => (
                                      <Listbox.Option
                                        key={airport.id}
                                        className={({ active }) =>
                                          classNames(
                                            active
                                              ? "text-white bg-red-600"
                                              : "text-gray-900",
                                            "relative cursor-default select-none py-2.5 pl-3 pr-9 text-sm",
                                          )
                                        }
                                        value={airport}
                                      >
                                        {({ selected, active }) => (
                                          <>
                                            <span
                                              className={classNames(
                                                selected
                                                  ? "font-semibold"
                                                  : "font-normal",
                                                "block truncate",
                                              )}
                                            >
                                              {`${airport.initials || airport.code || ""}${airport.initials || airport.code ? " · " : ""}${airport.name}`}
                                            </span>
                                            {selected ? (
                                              <span
                                                className={classNames(
                                                  active
                                                    ? "text-white"
                                                    : "text-red-600",
                                                  "absolute inset-y-0 right-0 flex items-center pr-4",
                                                )}
                                              >
                                                <CheckIcon
                                                  className="h-5 w-5"
                                                  aria-hidden="true"
                                                />
                                              </span>
                                            ) : null}
                                          </>
                                        )}
                                      </Listbox.Option>
                                    ))}
                                  </Listbox.Options>
                                </Transition>
                              </div>
                            </>
                          )}
                        </Listbox>

                        {airportFees.map((fee, index) => (
                          <div
                            key={`airport-fee-${fee.id || index}`}
                            className="mt-3 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-amber-950"
                          >
                            <ExclamationIcon
                              className="mt-0.5 h-5 w-5 shrink-0"
                              aria-hidden="true"
                            />
                            <div>
                              <h3 className="text-sm font-bold">
                                {FEE_LABELS[fee.type] || "Travel fee"}
                              </h3>
                              <p className="mt-1 text-xs leading-5 text-amber-900">
                                Projected fee:{" "}
                                {fee.is_percentage
                                  ? `${fee.fee}%`
                                  : formatCurrency(fee.fee, {
                                      allowZero: true,
                                    })}
                                . Included in the Step 2 pricing summary.
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="mt-1">
                        <Listbox
                          value={fboSelected}
                          onChange={handleFboSelectedChange}
                        >
                          {({ open }) => (
                            <>
                              <Listbox.Label className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">
                                FBO
                              </Listbox.Label>
                              <div className="relative mt-2">
                                <Listbox.Button
                                  className="relative w-full cursor-default rounded-lg border
                                                                    border-slate-300 bg-white py-2.5 pl-3 pr-10 text-left
                                                                    shadow-sm focus:border-sky-500 focus:outline-none
                                                                    focus:ring-1 focus:ring-sky-500 text-sm font-medium text-slate-800"
                                >
                                  <span className="block truncate">
                                    {fboSelected
                                      ? fboSelected.name
                                      : "Select FBO"}
                                  </span>
                                  <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                                    <ChevronUpDownIcon
                                      className="h-5 w-5 text-gray-400"
                                      aria-hidden="true"
                                    />
                                  </span>
                                </Listbox.Button>

                                <Transition
                                  show={open}
                                  as={Fragment}
                                  leave="transition ease-in duration-100"
                                  leaveFrom="opacity-100"
                                  leaveTo="opacity-0"
                                >
                                  <Listbox.Options className="absolute z-30 mt-2 max-h-64 w-full overflow-auto rounded-lg border border-slate-200 bg-white py-1 text-sm shadow-xl ring-1 ring-black/5 focus:outline-none">
                                    <div className="relative">
                                      <div className="sticky top-0 z-20 bg-white p-2">
                                        <div className="relative">
                                          <input
                                            type="text"
                                            name="search"
                                            id="fbo-search"
                                            placeholder="Search FBOs"
                                            value={fboSearchTerm}
                                            onChange={(e) =>
                                              setFboSearchTerm(e.target.value)
                                            }
                                            className="block h-10 w-full rounded-lg border-slate-300 bg-white pl-3 pr-16 text-sm font-normal text-slate-800 shadow-sm placeholder:text-slate-400 focus:border-sky-500 focus:ring-sky-500"
                                          />
                                          <div className="absolute inset-y-0 right-0 flex items-center pr-2">
                                            {fboSearchTerm && (
                                              <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                className="mr-1 h-5 w-5 cursor-pointer text-slate-400"
                                                viewBox="0 0 20 20"
                                                fill="currentColor"
                                                onClick={() => {
                                                  setFboSearchTerm("");
                                                }}
                                              >
                                                <path
                                                  fillRule="evenodd"
                                                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                                  clipRule="evenodd"
                                                />
                                              </svg>
                                            )}
                                            <svg
                                              xmlns="http://www.w3.org/2000/svg"
                                              className="h-5 w-5 text-slate-400"
                                              fill="none"
                                              viewBox="0 0 24 24"
                                              stroke="currentColor"
                                            >
                                              <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth="2"
                                                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                              />
                                            </svg>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                    {filteredFbos.map((fbo) => (
                                      <Listbox.Option
                                        key={fbo.id}
                                        className={({ active }) =>
                                          classNames(
                                            active
                                              ? "text-white bg-red-600"
                                              : "text-gray-900",
                                            "relative cursor-default select-none py-2.5 pl-3 pr-9 text-sm",
                                          )
                                        }
                                        value={fbo}
                                      >
                                        {({ selected, active }) => (
                                          <>
                                            <span
                                              className={classNames(
                                                selected
                                                  ? "font-semibold"
                                                  : "font-normal",
                                                "block truncate",
                                              )}
                                            >
                                              {fbo.name}
                                            </span>
                                            {selected ? (
                                              <span
                                                className={classNames(
                                                  active
                                                    ? "text-white"
                                                    : "text-red-600",
                                                  "absolute inset-y-0 right-0 flex items-center pr-4",
                                                )}
                                              >
                                                <CheckIcon
                                                  className="h-5 w-5"
                                                  aria-hidden="true"
                                                />
                                              </span>
                                            ) : null}
                                          </>
                                        )}
                                      </Listbox.Option>
                                    ))}
                                  </Listbox.Options>
                                </Transition>
                              </div>
                            </>
                          )}
                        </Listbox>

                        {fboFees.map((fee, index) => (
                          <div
                            key={`fbo-fee-${fee.id || index}`}
                            className="mt-3 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-amber-950"
                          >
                            <ExclamationIcon
                              className="mt-0.5 h-5 w-5 shrink-0"
                              aria-hidden="true"
                            />
                            <div>
                              <h3 className="text-sm font-bold">
                                Airport/Facility fee
                              </h3>
                              <p className="mt-1 text-xs leading-5 text-amber-900">
                                Projected commission:{" "}
                                {fee.is_percentage
                                  ? `${fee.fee}%`
                                  : formatCurrency(fee.fee, {
                                      allowZero: true,
                                    })}
                                . Recalculated from selected Regular Services.
                              </p>
                            </div>
                          </div>
                        ))}
                        {fboSelected && fboSelected.hours_of_operation && (
                          <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-4">
                            <div className="flex">
                              <div className="flex-shrink-0">
                                <ClockIcon
                                  className="h-5 w-5 text-slate-500"
                                  aria-hidden="true"
                                />
                              </div>
                              <div className="ml-3">
                                <h3 className="text-sm font-bold text-slate-800">
                                  Hours of operation
                                </h3>
                                <div className="mt-1 text-sm text-slate-600">
                                  {fboSelected.hours_of_operation}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="space-y-5 border-t border-slate-200 p-5 sm:p-6 md:border-l md:border-t-0">
                      <div>
                        <div className="flex items-center justify-between text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">
                          <span>Estimated Arrival</span>
                          {estimatedArrivalDate && (
                            <button
                              type="button"
                              onClick={() => setEstimatedArrivalDate(null)}
                              className="text-xs font-medium normal-case tracking-normal text-red-600 hover:text-red-700"
                            >
                              clear
                            </button>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={handleToggleEstimatedArrivalDate}
                          className="mt-2 inline-flex h-11 w-full items-center justify-between rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-800 shadow-sm hover:bg-slate-50"
                        >
                          <span>{formatWizardDate(estimatedArrivalDate)}</span>
                          <CalendarIcon
                            className="h-5 w-5 text-slate-400"
                            aria-hidden="true"
                          />
                        </button>
                        {estimatedArrivalDateOpen && (
                          <DatePicker
                            selected={estimatedArrivalDate}
                            onChange={(date) =>
                              handleEstimatedArrivalDateChange(date)
                            }
                            locale="pt-BR"
                            showTimeSelect
                            timeFormat="HH:mm"
                            timeIntervals={5}
                            dateFormat="MM/dd/yyyy HH:mm"
                            inline
                          />
                        )}
                        <label
                          htmlFor="onSite"
                          className="mt-2 flex cursor-pointer items-center gap-2 text-xs text-slate-500"
                        >
                          <input
                            id="onSite"
                            checked={onSite}
                            onChange={handleSetOnSite}
                            name="onSite"
                            type="checkbox"
                            className="h-4 w-4 rounded border-slate-300 text-red-600 focus:ring-red-500"
                          />
                          Aircraft is already on site
                        </label>
                      </div>

                      <div>
                        <label
                          htmlFor="ident"
                          className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500"
                        >
                          Call Sign
                        </label>

                        <p className="mt-1 text-xs leading-relaxed text-slate-500">
                          Shown when the aircraft may require FlightAware
                          assistance.
                        </p>

                        <div className="mt-2">
                          <input
                            type="text"
                            value={ident}
                            onChange={(e) =>
                              setIdent(e.target.value.toLocaleUpperCase())
                            }
                            name="ident"
                            id="ident"
                            placeholder="Not provided"
                            className="block h-11 w-full rounded-lg border-slate-300 text-sm font-medium text-slate-800 shadow-sm focus:border-sky-500 focus:ring-sky-500"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">
                          Estimated Departure
                          {estimatedDepartureDate && (
                            <span
                              onClick={() => setEstimatedDepartureDate(null)}
                              className="ml-2 underline text-xs text-red-500 cursor-pointer tracking-normal"
                            >
                              clear
                            </span>
                          )}
                        </label>
                        <button
                          type="button"
                          onClick={handleToggleEstimatedDepartureDate}
                          className="mt-2 inline-flex h-11 w-full items-center justify-between rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-800 shadow-sm hover:bg-slate-50"
                        >
                          <span>
                            {estimatedDepartureDate
                              ? formatWizardDate(estimatedDepartureDate)
                              : "No ETD yet"}
                          </span>
                          <CalendarIcon
                            className="h-5 w-5 text-slate-400"
                            aria-hidden="true"
                          />
                        </button>
                        {estimatedDepartureDateOpen && (
                          <DatePicker
                            selected={estimatedDepartureDate}
                            onChange={(date) =>
                              handleEstimatedDepartureDateChange(date)
                            }
                            locale="pt-BR"
                            showTimeSelect
                            timeFormat="HH:mm"
                            timeIntervals={5}
                            dateFormat="MM/dd/yyyy HH:mm"
                            inline
                          />
                        )}
                      </div>

                      <div>
                        <label className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">
                          Complete By
                          {completeByDate && (
                            <span
                              onClick={() => setCompleteByDate(null)}
                              className="ml-2 underline text-xs text-red-500 cursor-pointer tracking-normal"
                            >
                              clear
                            </span>
                          )}
                        </label>
                        <button
                          type="button"
                          onClick={handleToggleCompleteByDate}
                          className="mt-2 inline-flex h-11 w-full items-center justify-between rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-800 shadow-sm hover:bg-slate-50"
                        >
                          <span>{formatWizardDate(completeByDate)}</span>
                          <CalendarIcon
                            className="h-5 w-5 text-slate-400"
                            aria-hidden="true"
                          />
                        </button>
                        {completeByDateOpen && (
                          <DatePicker
                            selected={completeByDate}
                            onChange={(date) =>
                              handleCompleteByDateChange(date)
                            }
                            locale="pt-BR"
                            showTimeSelect
                            timeFormat="HH:mm"
                            timeIntervals={5}
                            dateFormat="MM/dd/yyyy HH:mm"
                            inline
                          />
                        )}
                      </div>

                      {(!currentUser.isCustomer ||
                        currentUser.promptRequestedBy) && (
                        <div>
                          <label
                            htmlFor="requestedBy"
                            className={`text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500 ${
                              currentUser.promptRequestedBytext
                                ? "text-gray-600"
                                : "text--gray-700"
                            }`}
                          >
                            Requested By
                          </label>
                          <div className="mt-2">
                            <input
                              type="text"
                              value={requestedBy}
                              onChange={(e) => setRequestedBy(e.target.value)}
                              name="requestedBy"
                              id="requestedBy"
                              placeholder="Enter your name and email address"
                              className="block h-11 w-full rounded-lg border-slate-300 text-sm font-medium text-slate-800 shadow-sm placeholder:font-normal focus:border-sky-500 focus:ring-sky-500"
                            />
                            {requestedByErrorMessage && (
                              <div className="flex gap-2">
                                <div className="flex-shrink-0 relative top-1">
                                  <InformationCircleIcon
                                    className="h-5 w-5 text-red-500"
                                    aria-hidden="true"
                                  />
                                </div>
                                <div>
                                  <p className="text-red-500 text-xs font-semibold mt-2">
                                    {requestedByErrorMessage}
                                  </p>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      <div>
                        <label
                          htmlFor="customerPurchaseOrder"
                          className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500"
                        >
                          Customer Purchase Order
                        </label>
                        <div className="mt-2">
                          <input
                            type="text"
                            value={customerPurchaseOrder}
                            onChange={(e) =>
                              setCustomerPurchaseOrder(e.target.value)
                            }
                            name="customerPurchaseOrder"
                            id="customerPurchaseOrder"
                            className="block h-11 w-full rounded-lg border-slate-300 text-sm font-medium text-slate-800 shadow-sm focus:border-sky-500 focus:ring-sky-500"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <aside className="h-fit rounded-xl border border-slate-200 bg-white p-5 shadow-sm lg:sticky lg:top-24">
                  <div className="flex items-center gap-3">
                    <span className="flex h-8 w-8 items-center justify-center rounded-full text-red-600">
                      <ClockIcon className="h-5 w-5" aria-hidden="true" />
                    </span>
                    <div>
                      <h2 className="text-lg font-bold text-slate-950">
                        Operational timeline
                      </h2>
                    </div>
                  </div>
                  <dl className="mt-5 space-y-4 border-l border-slate-200 pl-5">
                    <div>
                      <dt className="text-xs font-bold uppercase tracking-wider text-slate-400">
                        Arrival
                      </dt>
                      <dd className="mt-1 font-semibold text-slate-900">
                        {formatTimelineArrival(estimatedArrivalDate, onSite)}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs font-bold uppercase tracking-wider text-slate-400">
                        Complete by
                      </dt>
                      <dd className="mt-1 font-semibold text-slate-900">
                        {formatTimelineDate(completeByDate)}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs font-bold uppercase tracking-wider text-slate-400">
                        Departure
                      </dt>
                      <dd className="mt-1 font-semibold text-slate-900">
                        {formatTimelineDate(estimatedDepartureDate)}
                      </dd>
                    </div>
                  </dl>
                  {serviceWindowLabel && (
                    <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-bold text-emerald-800">
                      {serviceWindowLabel}
                    </div>
                  )}
                </aside>
              </div>
              <div className="mt-8 flex justify-end">
                <button
                  onClick={() => handleGoToNextStep(steps[0])}
                  className="inline-flex items-center rounded-lg border border-transparent bg-red-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-red-500 focus:ring-offset-2"
                >
                  Continue to services
                </button>
              </div>

              {serviceActivities.length > 0 && (
                <section className="mt-6 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                  <div className="flex items-center justify-between gap-4 border-b border-slate-100 px-5 py-4 sm:px-6">
                    <div>
                      <h2 className="text-base font-bold text-slate-950">
                        Tail History
                      </h2>
                      <p className="mt-0.5 text-sm text-slate-500">
                        {serviceActivities.length} previous{" "}
                        {serviceActivities.length === 1
                          ? "service"
                          : "services"}{" "}
                        found
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() =>
                        setShowServiceActivity(!showServiceActivity)
                      }
                      className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50"
                    >
                      {showServiceActivity ? "Hide" : "Show"}
                    </button>
                  </div>
                  {showServiceActivity && (
                    <>
                      <ul className="divide-y divide-slate-100 md:hidden">
                        {serviceActivities.map((service) => (
                          <li key={service.id} className="px-5 py-4 text-sm">
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <p className="font-semibold text-slate-900">
                                  {service.service_name}
                                </p>
                                <p className="mt-1 text-slate-500">
                                  {service.timestamp}
                                </p>
                              </div>
                              <Link
                                to={`/create-job/review/${service.job_id}`}
                                className="font-semibold text-blue-600 hover:text-blue-700"
                              >
                                {service.purchase_order}
                              </Link>
                            </div>
                            <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-slate-600">
                              <span>{service.airport_name}</span>
                              <span>{service.fbo_name}</span>
                              <span>{service.tail_number}</span>
                            </div>
                          </li>
                        ))}
                      </ul>
                      <div className="hidden overflow-x-auto md:block">
                        <table className="min-w-full divide-y divide-slate-200 text-sm">
                          <thead>
                            <tr>
                              <th
                                scope="col"
                                className="py-3 pl-6 pr-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500"
                              >
                                Date
                              </th>
                              <th
                                scope="col"
                                className="px-3 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500"
                              >
                                P.O
                              </th>
                              {!currentUser.isCustomer && (
                                <th
                                  scope="col"
                                  className="px-3 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500"
                                >
                                  Customer
                                </th>
                              )}
                              <th
                                scope="col"
                                className="px-3 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500"
                              >
                                Tail
                              </th>
                              <th
                                scope="col"
                                className="px-3 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500"
                              >
                                Airport
                              </th>
                              <th
                                scope="col"
                                className="px-3 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500"
                              >
                                FBO
                              </th>
                              <th
                                scope="col"
                                className="px-3 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500"
                              >
                                Service
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100 bg-white">
                            {serviceActivities.map((service) => (
                              <tr key={service.id}>
                                <td className="whitespace-nowrap py-3 pl-6 pr-3 text-slate-600">
                                  {service.timestamp}
                                </td>
                                <td className="whitespace-nowrap px-3 py-3 font-semibold text-blue-600">
                                  <Link
                                    to={`/create-job/review/${service.job_id}`}
                                  >
                                    {service.purchase_order}
                                  </Link>
                                </td>
                                {!currentUser.isCustomer && (
                                  <td className="whitespace-nowrap px-3 py-3 text-slate-600">
                                    <div className="w-60 truncate">
                                      {service.customer_name}
                                    </div>
                                  </td>
                                )}
                                <td className="whitespace-nowrap px-3 py-3 text-slate-600">
                                  {service.tail_number}
                                </td>
                                <td className="px-3 py-3 text-slate-600">
                                  <div className="w-40 truncate">
                                    {service.airport_name}
                                  </div>
                                </td>
                                <td className="whitespace-nowrap px-3 py-3 text-slate-600">
                                  {service.fbo_name}
                                </td>
                                <td className="whitespace-nowrap px-3 py-3 text-slate-600">
                                  <div className="w-96 truncate">
                                    {service.service_name}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </section>
              )}
            </>
          )}

          {isStepTwoSelected && (
            <div className="mt-6">
              <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_22rem]">
                <section className="order-2 min-w-0 space-y-4 lg:order-1">
                  {!isRetainerCustomer && (
                    <div className="rounded-xl border border-slate-200 bg-white px-5 pb-5 shadow-sm">
                      <div className="flex items-center gap-3 border-b border-slate-100 py-5">
                        <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-red-50 text-red-600">
                          <BriefcaseIcon
                            className="h-5 w-5"
                            aria-hidden="true"
                          />
                        </span>
                        <div>
                          <h3 className="font-bold text-slate-950">
                            Regular services
                          </h3>
                          <p className="text-sm text-slate-500">
                            Choose one or more services.
                          </p>
                        </div>
                      </div>
                      {renderRegularServicesContent()}
                    </div>
                  )}

                  {isRetainerCustomer && (
                    <>
                      {renderCatalogSection({
                        title: "Regular Services",
                        description: "Ad-hoc services requested for this job.",
                        icon: (
                          <BriefcaseIcon
                            className="h-6 w-6"
                            aria-hidden="true"
                          />
                        ),
                        isOpen: showRegularServices,
                        isDisabled: regularCatalogDisabled,
                        selectedCount: regularSelectedCount,
                        onToggle: toggleRegularServicesSection,
                        children: renderRegularServicesContent(),
                      })}
                      {renderCatalogSection({
                        title: "Monthly Retainer Services",
                        description:
                          "Services covered under the customer's monthly retainer.",
                        icon: (
                          <CalendarIcon
                            className="h-6 w-6"
                            aria-hidden="true"
                          />
                        ),
                        isOpen: showRetainerServices,
                        isDisabled: retainerCatalogDisabled,
                        selectedCount: retainerSelectedCount,
                        onToggle: toggleRetainerServicesSection,
                        children: renderRetainerServicesContent(),
                      })}
                      <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-left">
                        <div className="flex items-start gap-3">
                          <InformationCircleIcon
                            className="mt-0.5 h-5 w-5 shrink-0 text-blue-500"
                            aria-hidden="true"
                          />
                          <div>
                            <p className="text-sm font-semibold text-blue-900">
                              Use one service catalog per job.
                            </p>
                            <p className="mt-1 text-sm text-blue-800">
                              Selecting an item from one catalog temporarily
                              disables the other. Remove all selections to
                              switch catalogs.
                            </p>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </section>

                <aside className="order-1 h-fit rounded-xl border border-slate-200 bg-white p-5 shadow-sm lg:order-2 lg:sticky lg:top-24">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-xs font-bold uppercase tracking-[0.12em] text-slate-400">
                        Selection summary
                      </p>
                      <h3 className="mt-1 text-lg font-bold text-slate-950">
                        {selectedRegularServices.length +
                          selectedRetainerServices.length}{" "}
                        selected
                      </h3>
                    </div>
                    <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-red-50 text-red-600">
                      <BriefcaseIcon className="h-5 w-5" aria-hidden="true" />
                    </span>
                  </div>

                  {selectedRegularServices.length === 0 &&
                  selectedRetainerServices.length === 0 ? (
                    <div className="mt-5 rounded-lg border border-dashed border-slate-300 px-4 py-6 text-center text-sm text-slate-500">
                      Selected services will appear here.
                    </div>
                  ) : (
                    <ul className="mt-5 divide-y divide-slate-100">
                      {selectedRegularServices.map((service) => (
                        <li
                          key={`summary-service-${service.id}`}
                          className="flex items-start justify-between gap-3 py-3"
                        >
                          <div className="flex min-w-0 items-start gap-2.5">
                            <CheckCircleIcon
                              className="mt-0.5 h-5 w-5 shrink-0 bg-white text-emerald-600"
                              aria-hidden="true"
                            />
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-slate-800">
                                {service.name}
                              </p>
                              {canSeeProjectedPricing && (
                                <p className="mt-0.5 text-sm text-slate-500">
                                  {formatCurrency(
                                    projectedPriceByServiceId[
                                      String(service.id)
                                    ],
                                  )}
                                </p>
                              )}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleServiceChange(service)}
                            className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-red-600"
                            aria-label={`Remove ${service.name}`}
                          >
                            <XIcon className="h-4 w-4" aria-hidden="true" />
                          </button>
                        </li>
                      ))}
                      {selectedRetainerServices.map((service) => (
                        <li
                          key={`summary-retainer-${service.id}`}
                          className="flex items-start justify-between gap-3 py-3"
                        >
                          <div className="flex min-w-0 items-start gap-2.5">
                            <CheckCircleIcon
                              className="mt-0.5 h-5 w-5 shrink-0 bg-white text-emerald-600"
                              aria-hidden="true"
                            />
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-slate-800">
                                {service.name}
                              </p>
                              <p className="mt-0.5 text-xs font-semibold text-violet-700">
                                Covered by retainer
                              </p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRetainerServiceChange(service)}
                            className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-red-600"
                            aria-label={`Remove ${service.name}`}
                          >
                            <XIcon className="h-4 w-4" aria-hidden="true" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}

                  {canSeeProjectedPricing &&
                    selectedRegularServices.length > 0 && (
                      <div className="mt-4 border-t border-slate-200 pt-4">
                        {projectedPricingLoading && (
                          <p className="text-sm text-slate-500">
                            Calculating projected price…
                          </p>
                        )}
                        {projectedPricingError && (
                          <p className="text-sm text-red-600">
                            {projectedPricingError}
                          </p>
                        )}
                        {projectedPricing && !projectedPricingLoading && (
                          <dl className="text-sm">
                            <div className="flex justify-between gap-4 pb-3 text-slate-600">
                              <dt>Service subtotal</dt>
                              <dd className="font-semibold text-slate-900">
                                {formatCurrency(projectedPricing.servicesPrice)}
                              </dd>
                            </div>
                            {(projectedPricing.discounts || []).length > 0 && (
                              <div className="space-y-2 border-t border-slate-200 py-3">
                                {projectedPricing.discounts.map((discount) => (
                                  <div
                                    key={`discount-${discount.id}`}
                                    className="flex justify-between gap-4 text-emerald-700"
                                  >
                                    <dt>
                                      {DISCOUNT_LABELS[discount.name] ||
                                        "Discount"}
                                    </dt>
                                    <dd>
                                      {Number(
                                        discount.discount_dollar_amount,
                                      ) !== 0 && "-"}
                                      {formatCurrency(
                                        discount.discount_dollar_amount,
                                        { allowZero: true },
                                      )}
                                    </dd>
                                  </div>
                                ))}
                              </div>
                            )}
                            {(projectedPricing.additionalFees || []).length >
                              0 && (
                              <div className="space-y-2 border-t border-slate-200 py-3">
                                {projectedPricing.additionalFees.map((fee) => (
                                  <div
                                    key={`fee-${fee.id}`}
                                    className="flex justify-between gap-4 text-slate-600"
                                  >
                                    <dt>
                                      {FEE_LABELS[fee.name] || "Additional fee"}
                                    </dt>
                                    <dd>
                                      {formatCurrency(
                                        fee.additional_fee_dollar_amount,
                                        { allowZero: true },
                                      )}
                                    </dd>
                                  </div>
                                ))}
                              </div>
                            )}
                            <div className="flex justify-between gap-4 border-t border-slate-200 pt-3 text-base font-bold text-slate-950">
                              <dt>Projected total</dt>
                              <dd>
                                {formatCurrency(projectedPricing.totalPrice)}
                              </dd>
                            </div>
                          </dl>
                        )}
                      </div>
                    )}

                  {canSeeProjectedPricing &&
                    selectedRegularServices.length > 0 && (
                      <div className="mt-5 rounded-lg border border-amber-200 bg-amber-50 px-3 py-3 text-amber-900">
                        <p className="text-xs leading-5">
                          Projected pricing is shown for planning. Final values
                          follow price-list, fee, and override rules.
                        </p>
                      </div>
                    )}

                  {!currentUser.isCustomer &&
                    selectedRegularServices.length +
                      selectedRetainerServices.length >
                      0 && (
                      <div className="mt-3 flex items-start gap-2 text-slate-500">
                        <UserAddIcon
                          className="mt-0.5 h-4 w-4 shrink-0"
                          aria-hidden="true"
                        />
                        <p className="text-xs leading-5">
                          Vendor projected pricing becomes available after a
                          PM/vendor is selected.
                        </p>
                      </div>
                    )}

                  <div className="mt-5 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">
                    {serviceWindowLabel ||
                      "Add arrival and complete-before times to calculate the service window."}
                  </div>
                </aside>
              </div>

              <div className="mt-8 flex items-center justify-between gap-4">
                <button
                  type="button"
                  onClick={() => handleGotoPreviousStep(steps[1])}
                  className="inline-flex items-center justify-center rounded-lg border border-slate-300 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50"
                >
                  Back
                </button>
                <button
                  type="button"
                  onClick={() => handleGoToNextStep(steps[1])}
                  className="inline-flex items-center justify-center rounded-lg border border-transparent bg-red-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-red-700"
                >
                  Continue to instructions
                </button>
              </div>
            </div>
          )}

          {isStepThreeSelected && (
            <>
              <div className="mt-6 grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_22rem]">
                <section className="min-w-0 rounded-xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
                  <div>
                    <label
                      htmlFor="comment"
                      className="flex items-center gap-2 text-base font-semibold text-slate-900"
                    >
                      <OutlineInformationCircleIcon
                        className="h-4 w-4 shrink-0 text-red-500"
                        aria-hidden="true"
                      />
                      Job note
                    </label>
                    <div className="comment-editor mt-3 w-full overflow-hidden rounded-lg border border-slate-200 bg-white">
                      <CommentEditor
                        value={commentHtml}
                        onChange={(html, delta, source, editor) => {
                          setCommentHtml(html);
                          setComment(editor.getText().trim());
                        }}
                        placeholder="Write a note or update…"
                        inputId="comment"
                        inputLabel="Job note"
                      />
                    </div>
                  </div>

                  <div className="mt-7">
                    <h2 className="flex items-center gap-2 text-base font-semibold text-slate-900">
                      <PhotographIcon
                        className="h-5 w-5 text-red-600"
                        aria-hidden="true"
                      />
                      Photos
                      <span className="font-normal text-slate-400">
                        Optional
                      </span>
                    </h2>
                    <ImageUploading
                      multiple
                      acceptType={["jpg", "gif", "png", "jpeg"]}
                      value={images}
                      onChange={onChangePhoto}
                      maxNumber={10}
                      dataURLKey="data_url"
                    >
                      {({
                        imageList,
                        onImageUpload,
                        onImageRemoveAll,
                        onImageUpdate,
                        onImageRemove,
                        isDragging,
                        dragProps,
                        errors,
                      }) => (
                        <>
                          <div
                            onClick={onImageUpload}
                            className={classNames(
                              "mt-3 flex cursor-pointer justify-center rounded-xl border border-dashed bg-slate-50 px-6 py-8 transition hover:border-slate-400 hover:bg-slate-100",
                              isDragging
                                ? "border-red-400 bg-red-50"
                                : "border-slate-300",
                            )}
                            {...dragProps}
                          >
                            <div className="text-center">
                              <PhotographIcon
                                className="mx-auto h-9 w-9 text-slate-400"
                                aria-hidden="true"
                              />
                              <strong className="mt-2 block text-sm font-semibold text-slate-900">
                                Upload photos
                              </strong>
                              <p className="mt-1 text-xs text-slate-500">
                                PNG, JPG, or GIF · up to 10 MB · 10 photos max
                              </p>
                            </div>
                          </div>

                          {errors && (
                            <div className="mt-3 text-center text-sm font-medium text-red-600">
                              {errors.maxNumber && (
                                <span>You can only upload up to 10 photos</span>
                              )}
                              {errors.acceptType && (
                                <span>
                                  Your selected file type is not allowed
                                </span>
                              )}
                            </div>
                          )}

                          <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-4">
                            {imageList.map((image, index) => (
                              <div
                                key={index}
                                className="overflow-hidden rounded-lg border border-slate-200 bg-white"
                              >
                                <div className="cursor-pointer">
                                  <img
                                    className="h-28 w-full object-cover"
                                    src={image["data_url"]}
                                    alt=""
                                  />
                                </div>
                                <div className="flex justify-center gap-2 p-2">
                                  <button
                                    onClick={() => onImageUpdate(index)}
                                    className="rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                                  >
                                    Change
                                  </button>
                                  <button
                                    onClick={() => onImageRemove(index)}
                                    className="rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                                  >
                                    Remove
                                  </button>
                                  {/* <PencilIcon
                                onClick={() => onImageUpdate(index)}
                                className="flex-shrink-0 h-6 w-6 mr-3 cursor-pointer"
                              />
                              <TrashIcon
                                onClick={() => onImageRemove(index)}
                                className="flex-shrink-0 h-6 w-6 mr-2 cursor-pointer"
                              /> */}
                                </div>
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    </ImageUploading>
                  </div>

                  {(currentUser.isAdmin ||
                    currentUser.isSuperUser ||
                    currentUser.isInternalCoordinator ||
                    currentUser.isAccountManager) && (
                    <>
                      <div className="mt-7">
                        <h2 className="flex items-center gap-2 text-base font-semibold text-slate-900">
                          <TagIcon
                            className="h-5 w-5 text-red-600"
                            aria-hidden="true"
                          />
                          Tags
                        </h2>
                        <div className="mt-3 flex flex-wrap gap-2">
                          {tags.map((tag) => (
                            <button
                              type="button"
                              key={tag.id}
                              onClick={() => handleTagChange(tag)}
                              aria-pressed={tag.selected}
                              className={classNames(
                                "rounded-lg border px-3 py-2 text-sm font-medium transition",
                                tag.selected
                                  ? CREATE_JOB_TAG_STYLES[
                                      tag.tag_color || tag.color || "slate"
                                    ] || CREATE_JOB_TAG_STYLES.slate
                                  : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50",
                              )}
                            >
                              {tag.name}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="mt-7">
                        <h2 className="flex items-center gap-2 text-base font-semibold text-slate-900">
                          <UserGroupIcon
                            className="h-5 w-5 text-red-600"
                            aria-hidden="true"
                          />
                          Followers
                        </h2>
                        <p className="mt-1 text-sm text-slate-500">
                          Followers receive job-creation and completion
                          notifications.
                        </p>

                        <div className="mt-3">
                          <div className="flex gap-2">
                            <div className="min-w-0 flex-1">
                              <input
                                type="text"
                                value={customerFollowerEmailSearchTerm}
                                onChange={(e) =>
                                  setCustomerFollowerEmailSearchTerm(
                                    e.target.value,
                                  )
                                }
                                placeholder="Enter one valid email address"
                                className="block h-11 w-full rounded-lg border-slate-300 text-sm shadow-sm placeholder:text-slate-400 focus:border-sky-500 focus:ring-sky-500"
                              />
                            </div>
                            <button
                              type="button"
                              onClick={() => handleAddFollowerEmail()}
                              className="inline-flex h-11 shrink-0 items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50"
                            >
                              <PlusIcon
                                className="h-4 w-4"
                                aria-hidden="true"
                              />
                              Add
                            </button>
                          </div>
                          {customerFollowerEmails.length > 0 && (
                            <div className="mt-4 text-xs font-bold uppercase tracking-wider text-slate-400">
                              Suggestions
                            </div>
                          )}
                          <div className="max-h-48 overflow-y-auto">
                            <ul
                              role="list"
                              className="mt-1 divide-y divide-slate-100"
                            >
                              {customerFollowerEmails.map((email) => (
                                <li
                                  key={email.id}
                                  className="flex items-center justify-between gap-3 py-2"
                                >
                                  <p className="min-w-0 truncate text-sm text-slate-600">
                                    {email.email}
                                  </p>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      handleAddFollowerEmail(email)
                                    }
                                    className="rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                                  >
                                    Add
                                  </button>
                                </li>
                              ))}
                            </ul>
                          </div>

                          {jobFollowerEmails.length > 0 && (
                            <div className="mt-4 text-xs font-bold uppercase tracking-wider text-slate-400">
                              Selected Emails
                            </div>
                          )}
                          <div className="max-h-48 overflow-y-auto">
                            <ul role="list" className="mt-2 space-y-2">
                              {jobFollowerEmails.map((email, index) => (
                                <li
                                  key={index}
                                  className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2"
                                >
                                  <div className="flex min-w-0 items-center gap-2">
                                    <p className="truncate text-sm text-slate-700">
                                      {email.email}
                                    </p>
                                    {email.is_persistent && (
                                      <span className="inline-flex rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-700">
                                        Persistent
                                      </span>
                                    )}
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      handleRemoveFollowerEmail(email)
                                    }
                                    className="rounded-md p-1 text-slate-400 hover:bg-white hover:text-slate-700"
                                  >
                                    <span className="sr-only">
                                      Remove {email.email}
                                    </span>
                                    <XIcon
                                      className="h-4 w-4"
                                      aria-hidden="true"
                                    />
                                  </button>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </section>

                <aside className="h-fit rounded-xl border border-slate-200 bg-white p-5 shadow-sm lg:order-none lg:sticky lg:top-24">
                  <h2 className="text-lg font-bold text-slate-950">
                    Ready to create
                  </h2>

                  <div className="mt-5 space-y-4 text-sm">
                    <div className="flex gap-3">
                      <AirplaneIcon
                        className="mt-0.5 h-5 w-5 shrink-0 text-slate-400"
                        aria-hidden="true"
                      />
                      <div className="min-w-0">
                        <p className="font-bold text-slate-900">
                          {tailNumber || "Tail not specified"}
                        </p>
                        <p className="mt-0.5 break-words leading-5 text-slate-500">
                          {customerSelected?.name ||
                            currentUser.customerName ||
                            "Customer not specified"} ·{" "}
                          {aircraftTypeSelected?.name ||
                            "Aircraft not specified"}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <LocationMarkerIcon
                        className="mt-0.5 h-5 w-5 shrink-0 text-slate-400"
                        aria-hidden="true"
                      />
                      <p className="min-w-0 text-slate-600">
                        <span className="font-semibold text-slate-900">
                          {airportSelected?.initials ||
                            airportSelected?.code ||
                            airportSelected?.name ||
                            "Airport not specified"}
                        </span>
                        {fboSelected?.name
                          ? ` · ${fboSelected.name}`
                          : " · FBO not specified"}
                      </p>
                    </div>
                    <div className="flex gap-3">
                      <BriefcaseIcon
                        className="mt-0.5 h-5 w-5 shrink-0 text-slate-400"
                        aria-hidden="true"
                      />
                      <p className="text-slate-600">
                        <span className="font-semibold text-slate-900">
                          {selectedRegularServices.length +
                            selectedRetainerServices.length}{" "}
                          {selectedRetainerServices.length > 0
                            ? "Retainer"
                            : "Regular"}{" "}
                          {selectedRegularServices.length +
                            selectedRetainerServices.length ===
                          1
                            ? "Service"
                            : "Services"}
                        </span>
                        {canSeeProjectedPricing &&
                        selectedRegularServices.length > 0 &&
                        projectedPricing
                          ? ` · ${formatCurrency(projectedPricing.totalPrice)} projected`
                          : ""}
                      </p>
                    </div>
                    <div className="flex gap-3 rounded-lg bg-amber-50 px-3 py-3 text-amber-900">
                      <KeyIcon
                        className="mt-0.5 h-5 w-5 shrink-0 text-amber-600"
                        aria-hidden="true"
                      />
                      <div className="min-w-0 flex-1">
                        <p className="text-[11px] font-bold uppercase tracking-[0.08em] text-amber-700">
                          Key location
                        </p>
                        {keyLocationLoading ? (
                          <p className="mt-1 text-sm text-amber-800">
                            Checking the aircraft record…
                          </p>
                        ) : keyLocation ? (
                          <p className="mt-1 whitespace-pre-line break-words text-sm font-semibold leading-5 text-amber-950">
                            {keyLocation}
                          </p>
                        ) : (
                          <p className="mt-1 text-sm leading-5 text-amber-800">
                            No key location is saved for this tail.
                          </p>
                        )}
                        {!keyLocationLoading &&
                          (keyLocationAttachments.length > 0 ||
                            keyLocationAttachment) && (
                            <div className="mt-3 border-t border-amber-200 pt-3">
                              <p className="text-[11px] font-bold uppercase tracking-[0.08em] text-amber-700">
                                Attachment
                              </p>
                              <ul className="mt-2 space-y-1.5">
                                {keyLocationAttachments.map((attachment) => (
                                  <li
                                    key={attachment.id}
                                    className="flex min-w-0 items-center gap-2 text-sm text-amber-950"
                                  >
                                    <PaperClipIcon
                                      className="h-4 w-4 shrink-0 text-amber-600"
                                      aria-hidden="true"
                                    />
                                    <span className="min-w-0 flex-1 truncate font-semibold">
                                      {attachment.name}
                                    </span>
                                    <a
                                      href={getKeyLocationAttachmentUrl(
                                        attachment,
                                      )}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="shrink-0 text-xs font-bold text-sky-700 hover:text-sky-900 hover:underline"
                                    >
                                      View
                                    </a>
                                  </li>
                                ))}
                                {keyLocationAttachment && (
                                  <li className="flex min-w-0 items-center gap-2 text-sm text-amber-950">
                                    <PaperClipIcon
                                      className="h-4 w-4 shrink-0 text-amber-600"
                                      aria-hidden="true"
                                    />
                                    <span className="min-w-0 flex-1 truncate font-semibold">
                                      {keyLocationAttachment.name}
                                    </span>
                                    <span className="shrink-0 text-xs font-medium text-amber-700">
                                      Ready to upload
                                    </span>
                                  </li>
                                )}
                              </ul>
                            </div>
                          )}
                        {!keyLocationLoading && (
                          <button
                            type="button"
                            onClick={handleOpenKeyLocationEditor}
                            className="mt-2 text-xs font-bold text-amber-800 hover:text-amber-950 hover:underline"
                          >
                            {keyLocation
                              ? "Confirm or edit"
                              : "Add key location and attachment"}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 space-y-3">
                    {!currentUser.isCustomer && (
                      <>
                        <button
                          type="button"
                          onClick={() => createJob()}
                          className="inline-flex w-full items-center justify-center rounded-lg border border-slate-300 bg-slate-100 px-4 py-3 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-200"
                        >
                          Create unassigned job
                        </button>
                        <button
                          type="button"
                          onClick={() => createJob("assignments")}
                          className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-transparent bg-red-600 px-4 py-3 text-sm font-semibold text-white shadow-sm hover:bg-red-700"
                        >
                          <UserAddIcon className="h-5 w-5" aria-hidden="true" />
                          Continue with Assignment
                        </button>
                        <p className="text-center text-xs leading-5 text-slate-500">
                          Create the job first, then continue to its assignment
                          workspace.
                        </p>
                      </>
                    )}
                    {currentUser.isCustomer && (
                      <button
                        type="button"
                        onClick={() => createJob()}
                        className="inline-flex w-full items-center justify-center rounded-lg border border-transparent bg-red-600 px-4 py-3 text-sm font-semibold text-white shadow-sm hover:bg-red-700"
                      >
                        Create Job
                      </button>
                    )}
                  </div>
                </aside>
              </div>
              <div className="mt-6">
                <button
                  type="button"
                  onClick={() => handleGotoPreviousStep(steps[2])}
                  className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50"
                >
                  <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" />
                  Back to services
                </button>
              </div>
            </>
          )}
        </main>
      )}

      <Transition.Root show={Boolean(activeServiceScope)} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-[11000]"
          onClose={() => setActiveServiceScope(null)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-in-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in-out duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-slate-900/30 backdrop-blur-[1px]" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-hidden">
            <div className="absolute inset-0 overflow-hidden">
              <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-8 sm:pl-12">
                <Transition.Child
                  as={Fragment}
                  enter="transform transition ease-in-out duration-300"
                  enterFrom="translate-x-full"
                  enterTo="translate-x-0"
                  leave="transform transition ease-in-out duration-300"
                  leaveFrom="translate-x-0"
                  leaveTo="translate-x-full"
                >
                  <Dialog.Panel className="pointer-events-auto w-screen max-w-sm bg-white shadow-xl sm:max-w-md">
                    <div className="flex h-full flex-col overflow-y-auto">
                      <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-5 sm:px-6">
                        <div className="min-w-0">
                          <Dialog.Title className="text-lg font-bold text-slate-950">
                            {activeServiceScope?.service?.name}
                          </Dialog.Title>
                          <p className="mt-1 text-sm leading-5 text-slate-500">
                            Scope of work · review without expanding every
                            service
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => setActiveServiceScope(null)}
                          className="shrink-0 rounded-lg border border-red-300 bg-white p-1.5 text-slate-600 shadow-sm hover:bg-red-50 hover:text-red-700"
                        >
                          <span className="sr-only">Close</span>
                          <XIcon className="h-5 w-5" aria-hidden="true" />
                        </button>
                      </div>

                      <div className="space-y-7 px-5 py-6 sm:px-6">
                        <div
                          className="rounded-xl border border-sky-100 bg-sky-50 px-4 py-4 text-sm leading-6 text-slate-700"
                          dangerouslySetInnerHTML={{
                            __html:
                              activeServiceScope?.service?.description?.replace(
                                /\r\n|\n/g,
                                "<br />",
                              ) || "No scope of work has been provided.",
                          }}
                        />

                        <div>
                          <h3 className="text-sm font-bold text-slate-950">
                            Operational expectations
                          </h3>
                          <ul className="mt-4 space-y-3 text-sm leading-5 text-slate-600">
                            {[
                              "Complete the Initial Inspection before beginning service.",
                              "Document pre-existing conditions and relevant limitations.",
                              "Complete Final Inspection and required completion photos.",
                            ].map((expectation) => (
                              <li
                                key={expectation}
                                className="flex items-start gap-2.5"
                              >
                                <CheckCircleIcon
                                  className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600"
                                  aria-hidden="true"
                                />
                                <span>{expectation}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      <KeyLocationEditorDrawer
        open={keyLocationEditorOpen}
        onClose={() => setKeyLocationEditorOpen(false)}
        value={keyLocationDraft}
        onChange={setKeyLocationDraft}
        hasExistingLocation={Boolean(keyLocation)}
        attachments={keyLocationAttachmentsDraft}
        selectedAttachment={keyLocationAttachmentDraft}
        onAttachmentChange={setKeyLocationAttachmentDraft}
        onRemoveAttachment={(attachment) => {
          setKeyLocationAttachmentsDraft((current) =>
            current.filter((item) => item.id !== attachment.id),
          );
          setRemovedKeyLocationAttachmentIdsDraft((current) => [
            ...current,
            attachment.id,
          ]);
        }}
        onRemoveSelectedAttachment={() =>
          setKeyLocationAttachmentDraft(null)
        }
        onSave={() => {
          setKeyLocation(keyLocationDraft.trim());
          setKeyLocationAttachments(keyLocationAttachmentsDraft);
          setKeyLocationAttachment(keyLocationAttachmentDraft);
          setRemovedKeyLocationAttachmentIds(
            removedKeyLocationAttachmentIdsDraft,
          );
          setKeyLocationDirty(true);
          setKeyLocationEditorOpen(false);
        }}
      />

      {/* Tail Alert Notification */}
      {createPortal(
        <div
          aria-live="assertive"
          className="job-create-redesign pointer-events-none fixed inset-0 z-[12000] flex items-end px-4 py-6 sm:items-start sm:p-6"
        >
          <div className="flex w-full flex-col items-center space-y-4 sm:items-end">
            {/* Notification panel, dynamically insert this into the live region when it needs to be displayed */}
            <Transition
              show={showTailAlert}
              as={Fragment}
              enter="transform ease-out duration-300 transition"
              enterFrom="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
              enterTo="translate-y-0 opacity-100 sm:translate-x-0"
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="pointer-events-auto w-full max-w-sm overflow-hidden rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5">
                <div className="p-4">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <InboxIcon
                        className="h-6 w-6 text-gray-400"
                        aria-hidden="true"
                      />
                    </div>
                    <div className="ml-3 w-0 flex-1 pt-0.5">
                      <p className="text-base font-semibold text-slate-900">
                        Tail Number Alert
                      </p>
                      <p className="mt-1 text-sm leading-5 text-slate-600">
                        {tailAlert?.message}
                      </p>
                      <div className="mt-4 flex gap-2">
                        <button
                          type="button"
                          onClick={() => deleteTailAlert()}
                          className="rounded-lg border border-red-200 bg-white px-3 py-2 text-sm font-semibold
                                            text-red-600 hover:bg-red-50 focus:outline-none
                                            focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                        >
                          Delete
                        </button>
                        <button
                          onClick={() => {
                            setShowTailAlert(false);
                          }}
                          type="button"
                          className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold
                                                 text-slate-700 hover:bg-slate-50 focus:outline-none
                                                  focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                        >
                          Dismiss
                        </button>
                      </div>
                    </div>
                    <div className="ml-4 flex flex-shrink-0">
                      <button
                        type="button"
                        className="inline-flex rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                        onClick={() => {
                          setShowTailAlert(false);
                        }}
                      >
                        <span className="sr-only">Close</span>
                        <XCircleIcon className="h-5 w-5" aria-hidden="true" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </Transition>
            <Transition
              show={showTailOpenJobAlert}
              as={Fragment}
              enter="transform ease-out duration-300 transition"
              enterFrom="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
              enterTo="translate-y-0 opacity-100 sm:translate-x-0"
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="pointer-events-auto w-full max-w-sm overflow-hidden rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5">
                <div className="p-4">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <InboxIcon
                        className="h-6 w-6 text-gray-400"
                        aria-hidden="true"
                      />
                    </div>
                    <div className="ml-3 w-0 flex-1 pt-0.5">
                      <p className="text-base font-semibold text-slate-900">
                        Tail Open Job Alert
                      </p>
                      <p className="mt-1 text-sm leading-5 text-slate-600">
                        There is an open job for this tail. Refer to Job{" "}
                        <Link
                          to={`/jobs/${tailOpenJobAlert?.jobId}/details`}
                          className="text-blue-500 font-medium"
                        >
                          {tailOpenJobAlert?.purchaseOrder}
                        </Link>
                      </p>
                      <div className="mt-4 flex">
                        <button
                          onClick={() => {
                            setShowTailOpenJobAlert(false);
                          }}
                          type="button"
                          className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold
                                                 text-slate-700 hover:bg-slate-50 focus:outline-none
                                                  focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                        >
                          Dismiss
                        </button>
                      </div>
                    </div>
                    <div className="ml-4 flex flex-shrink-0">
                      <button
                        type="button"
                        className="inline-flex rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                        onClick={() => {
                          setShowTailOpenJobAlert(false);
                        }}
                      >
                        <span className="sr-only">Close</span>
                        <XCircleIcon className="h-5 w-5" aria-hidden="true" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </Transition>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
};

export default CreateJob;
